import React, { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import IWMI_Logo from "../assets/IWMI_Logo.png";
import CGIAR_transparent from "../assets/cgiar_transparent.png";

const COUNTRIES_API_URL = "/api/countries";
const BASIN_ANIMATION_API_URL =
  "/api/basin/animation";

const BASINS = [];

const TIME_STEPS = [
  { value: "LTMA", label: "Long-term mean annual" },
  { value: "LTMM", label: "Long-term mean monthly" },
  { value: "Annual", label: "Annual" },
  { value: "Monthly", label: "Monthly" },
];

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];


const RIVER_ANIMATION_MODES = [
  {
    value: "fixed-year-months",
    label: "Monthly progression · selected year",
    shortLabel: "Months in year",
  },
  {
    value: "fixed-month-years",
    label: "Same month · across years",
    shortLabel: "Month across years",
  },
  {
    value: "annual-years",
    label: "Annual progression · across years",
    shortLabel: "Annual across years",
  },
];


const extractRiverAnimationData = (html) => {
  if (!html || typeof html !== "string") return null;

  const marker = "const data =";
  const markerIndex = html.indexOf(marker);

  if (markerIndex === -1) return null;

  const objectStart = html.indexOf("{", markerIndex + marker.length);
  if (objectStart === -1) return null;

  const jsonText = readBalancedJson(html, objectStart);
  if (!jsonText) return null;

  try {
    const parsed = JSON.parse(jsonText);

    return Array.isArray(parsed?.segments)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const enhanceRiverAnimationHtml = (
  html,
  {
    periodLabel = "",
    hideGeneratedPlayback = true,
  } = {}
) => {
  if (!html || typeof html !== "string") return "";

  const safePeriodLabel = JSON.stringify(periodLabel);
  const safeHidePlayback = JSON.stringify(Boolean(hideGeneratedPlayback));

  const styles = `
    <style id="watertracker-river-animation-enhancements">
      .watertracker-hidden-generated-control {
        display: none !important;
      }

      .watertracker-extent-control {
        clear: both;
      }

      .watertracker-extent-control a {
        width: 30px !important;
        height: 30px !important;
        line-height: 30px !important;
        display: block !important;
        text-align: center !important;
        background: #ffffff !important;
        color: #102033 !important;
        text-decoration: none !important;
        font-size: 17px !important;
        font-weight: 800 !important;
        cursor: pointer !important;
        border-bottom: 1px solid #ccc !important;
      }

      .watertracker-extent-control a:hover {
        background: #f4f7f9 !important;
      }
    </style>
  `;

  const script = `
    <script id="watertracker-river-animation-controller">
      (function () {
        var currentPeriodLabel = ${safePeriodLabel};
        var hideGeneratedPlayback = ${safeHidePlayback};

        function getExactButton(texts) {
          return Array.from(document.querySelectorAll("button")).find(function (button) {
            var value = (button.textContent || "").trim().toLowerCase();

            return texts.some(function (text) {
              return value === text.toLowerCase();
            });
          });
        }

        function hideOnlyGeneratedHeaderControls() {
          if (!hideGeneratedPlayback) return;

          ["Previous", "Pause", "Play", "Resume", "Next"].forEach(function (label) {
            var button = getExactButton([label]);

            if (button && !button.closest(".leaflet-control")) {
              button.classList.add("watertracker-hidden-generated-control");
            }
          });

          var fitButton = getExactButton([
            "Fit basin",
            "Fit to basin",
            "Extent",
            "Fit to extent",
            "⛶ Extent"
          ]);

          if (fitButton && !fitButton.closest(".leaflet-control")) {
            fitButton.classList.add("watertracker-hidden-generated-control");
          }

          if (currentPeriodLabel) {
            Array.from(document.querySelectorAll("button, span, div"))
              .filter(function (element) {
                if (element.childElementCount !== 0) return false;

                var value = (element.textContent || "").trim();

                return (
                  /^(January|February|March|April|May|June|July|August|September|October|November|December)\\s+\\d{4}$/i.test(value) ||
                  /^\d{4}$/.test(value)
                );
              })
              .forEach(function (element) {
                if (
                  element.closest("header") &&
                  !element.closest(".leaflet-control")
                ) {
                  element.style.display = "none";
                }
              });
          }
        }

        function findMapInstance() {
          var candidates = [
            window.map,
            window.leafletMap,
            window.riverMap,
            window.basinMap
          ];

          var direct = candidates.find(function (item) {
            return item && typeof item.fitBounds === "function";
          });

          if (direct) return direct;

          // Try objects stored on window.
          for (var key in window) {
            try {
              var candidate = window[key];

              if (
                candidate &&
                typeof candidate === "object" &&
                typeof candidate.fitBounds === "function" &&
                typeof candidate.getBounds === "function"
              ) {
                return candidate;
              }
            } catch (error) {}
          }

          return null;
        }

        function triggerExistingFitButton() {
          var fitButton = getExactButton([
            "Fit basin",
            "Fit to basin",
            "Extent",
            "Fit to extent",
            "⛶ Extent"
          ]);

          if (fitButton) {
            fitButton.click();
            return true;
          }

          return false;
        }

        function fitExtent() {
          if (triggerExistingFitButton()) return;

          var map = findMapInstance();

          if (!map) return;

          try {
            if (window.BASIN_BOUNDS) {
              map.fitBounds(window.BASIN_BOUNDS);
              return;
            }

            if (window.basinBounds) {
              map.fitBounds(window.basinBounds);
              return;
            }

            if (window.riverBounds) {
              map.fitBounds(window.riverBounds);
              return;
            }

            // Last useful fallback: refit to current layer bounds where exposed.
            var layerCandidates = [
              window.riverLayer,
              window.basinLayer,
              window.flowLayer,
              window.geojsonLayer
            ];

            var layer = layerCandidates.find(function (item) {
              return item && typeof item.getBounds === "function";
            });

            if (layer) {
              map.fitBounds(layer.getBounds(), { padding: [18, 18] });
            }
          } catch (error) {
            console.warn("Could not fit basin extent:", error);
          }
        }

        function addExtentBelowZoom() {
          if (document.getElementById("watertracker-map-extent-control")) {
            return;
          }

          var zoomControl = document.querySelector(
            ".leaflet-control-zoom"
          );

          if (!zoomControl) return;

          var wrapper = document.createElement("div");
          wrapper.id = "watertracker-map-extent-control";
          wrapper.className =
            "leaflet-control watertracker-extent-control leaflet-bar";

          var button = document.createElement("a");
          button.href = "#";
          button.title = "Fit to basin extent";
          button.setAttribute("aria-label", "Fit to basin extent");
          button.innerHTML = "⛶";

          button.onclick = function (event) {
            event.preventDefault();
            event.stopPropagation();
            fitExtent();
            return false;
          };

          wrapper.appendChild(button);

          // Put it immediately after Leaflet's normal +/- control.
          zoomControl.parentNode.insertBefore(
            wrapper,
            zoomControl.nextSibling
          );
        }

        function applyRiverFlowUnit() {
          Array.from(
            document.querySelectorAll(".metric-label")
          ).forEach(function (label) {
            var value =
              (label.textContent || "")
                .trim()
                .toLowerCase();

            if (
              value === "flow value" ||
              value.indexOf("flow value (") === 0
            ) {
              label.textContent = "Flow value (km³)";
            }

            if (
              value === "static reference flow" ||
              value.indexOf("static reference flow (") === 0
            ) {
              label.textContent =
                "Static reference flow (km³)";
            }
          });
        }

        function run() {
          hideOnlyGeneratedHeaderControls();
          addExtentBelowZoom();
          applyRiverFlowUnit();

          window.setTimeout(function () {
            hideOnlyGeneratedHeaderControls();
            addExtentBelowZoom();
            applyRiverFlowUnit();
          }, 250);

          window.setTimeout(function () {
            hideOnlyGeneratedHeaderControls();
            addExtentBelowZoom();
            applyRiverFlowUnit();
          }, 700);

          window.setTimeout(function () {
            hideOnlyGeneratedHeaderControls();
            addExtentBelowZoom();
            applyRiverFlowUnit();
          }, 1400);
        }

        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", run);
        } else {
          run();
        }
      })();
    </script>
  `;

  let updatedHtml = html;

  if (updatedHtml.includes("</head>")) {
    updatedHtml = updatedHtml.replace("</head>", `${styles}</head>`);
  } else {
    updatedHtml = `${styles}${updatedHtml}`;
  }

  if (updatedHtml.includes("</body>")) {
    updatedHtml = updatedHtml.replace("</body>", `${script}</body>`);
  } else {
    updatedHtml = `${updatedHtml}${script}`;
  }

  return updatedHtml;
};

const DATA_START_YEAR = 2000;
const DATA_END_YEAR = 2024;

const YEARS = Array.from(
  { length: DATA_END_YEAR - DATA_START_YEAR + 1 },
  (_, i) => String(DATA_START_YEAR + i)
).reverse();

// Basin years are discovered dynamically from the selected basin metadata.
// Country keeps the original 2000-2024 range above.
const BASIN_YEARS = [];

const palette = {
  panel: "rgba(255,255,255,0.94)",
  panelSolid: "#ffffff",
  border: "rgba(16, 32, 51, 0.09)",
  borderStrong: "rgba(2, 151, 166, 0.22)",
  primary: "#28537d",
  primarySoft: "#e8f4f8",
  accent: "#46bbd4",
  teal: "#0297a6",
  text: "#102033",
  textSoft: "#52677d",
  textMuted: "#7f93a8",
  dangerBg: "#fff4f2",
  dangerBorder: "#ffd0c4",
  dangerText: "#b94222",
  shadow: "0 24px 70px rgba(8, 26, 44, 0.10)",
  shadowSoft: "0 10px 34px rgba(8, 26, 44, 0.06)",
  innerShadow: "inset 0 1px 0 rgba(255,255,255,0.78)",
};

const getDisplayBasinCoordinates = (coordinates, nodeCount) => {
  if (!Number.isFinite(Number(nodeCount)) || nodeCount <= 0) return null;

  const count = Number(nodeCount);
  const hasX = Array.isArray(coordinates?.x) && coordinates.x.length === count;
  const hasY = Array.isArray(coordinates?.y) && coordinates.y.length === count;

  if (!hasX || !hasY) {
    return {
      x: Array.from({ length: count }, (_, i) =>
        count === 1 ? 0.5 : 0.08 + (i / (count - 1)) * 0.84
      ),
      y: Array.from({ length: count }, () => 0.5),
      source: "fallback",
    };
  }

  const x = coordinates.x.map((v) => Number(v));
  const y = coordinates.y.map((v) => Number(v));
  const valid = [...x, ...y].filter(Number.isFinite);

  if (!valid.length) return null;

  // WaterTracker/YAML coordinates are already normalized coordinates.
  // Preserve them exactly whenever they are valid; do not independently
  // stretch the X and Y axes because that changes the basin geometry.
  const normalized = valid.every((v) => v >= 0 && v <= 1);
  if (normalized) return { x, y, source: "backend" };

  // Defensive fallback for older coordinate files that may contain pixel-
  // like values. Scale both axes into the same safe display box.
  const normalize = (values) => {
    const finite = values.filter(Number.isFinite);
    if (!finite.length) return values.map(() => 0.5);
    const min = Math.min(...finite);
    const max = Math.max(...finite);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) {
      return values.map(() => 0.5);
    }
    return values.map((v) =>
      Number.isFinite(v) ? 0.08 + ((v - min) / (max - min)) * 0.84 : 0.5
    );
  };

  return { x: normalize(x), y: normalize(y), source: "scaled" };
};

const normalizeBasinNodeName = (value) =>
  String(
    value && typeof value === "object"
      ? value.name ?? value.country ?? value.label ?? value.id ?? ""
      : value ?? ""
  )
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getBasinDisplayCoordinates = (coordinates, nodeLabelsOrCount, countryOrder = []) => {
  const labels = Array.isArray(nodeLabelsOrCount)
    ? nodeLabelsOrCount
    : null;
  const count = labels ? labels.length : Number(nodeLabelsOrCount);

  if (!Number.isFinite(count) || count <= 0) return null;

  const rawX = coordinates?.x;
  const rawY = coordinates?.y;
  if (!Array.isArray(rawX) || !Array.isArray(rawY) || rawX.length !== rawY.length) {
    return null;
  }

  const validCoordinate = (value) =>
    value === null || value === undefined || Number.isFinite(Number(value));
  if (!rawX.every(validCoordinate) || !rawY.every(validCoordinate)) return null;

  const xValues = rawX.map((value) =>
    value === null || value === undefined ? null : Number(value)
  );
  const yValues = rawY.map((value) =>
    value === null || value === undefined ? null : Number(value)
  );

  // Some WaterTracker APIs return one YAML coordinate per country, while the
  // Sankey node list also includes non-country/source/sink nodes. Map country
  // coordinates by name instead of rejecting the entire layout or shifting
  // coordinate indexes. The original YAML x/y values are never modified.
  if (labels && xValues.length !== count) {
    if (!Array.isArray(countryOrder) || countryOrder.length !== xValues.length) {
      return null;
    }

    const countryCoordinateByName = new Map();
    countryOrder.forEach((country, index) => {
      const key = normalizeBasinNodeName(country);
      if (key && !countryCoordinateByName.has(key)) {
        countryCoordinateByName.set(key, { x: xValues[index], y: yValues[index] });
      }
    });

    const mappedX = [];
    const mappedY = [];
    let matchedCountryCount = 0;

    labels.forEach((label) => {
      const key = normalizeBasinNodeName(label);
      const coordinate = countryCoordinateByName.get(key);
      if (coordinate) {
        mappedX.push(coordinate.x);
        mappedY.push(coordinate.y);
        matchedCountryCount += 1;
      } else {
        // Non-country nodes are deliberately left unpositioned. Plotly can
        // place those nodes without changing the supplied country coordinates.
        mappedX.push(null);
        mappedY.push(null);
      }
    });

    if (!matchedCountryCount) return null;
    return {
      x: mappedX,
      y: mappedY,
      source: "wtt_node_coord.yml-country-map",
    };
  }

  if (xValues.length !== count) return null;

  return {
    x: xValues,
    y: yValues,
    source: "wtt_node_coord.yml",
  };
};

const BASIN_VIBRANT_COLORS = [
  "#2F80ED",
  "#9B51E0",
  "#00A896",
  "#F26430",
  "#F2B134",
  "#5AA647",
  "#D94F70",
  "#3D7CC9",
  "#F28C28",
  "#6B9E3E",
];

const hexToRgba = (hex, alpha = 0.58) => {
  const value = String(hex || "").replace("#", "");
  if (value.length !== 6) return `rgba(70,120,170,${alpha})`;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const getBasinNodeColors = (labels = [], existing = []) => {
  return labels.map((label, index) =>
    BASIN_VIBRANT_COLORS[index % BASIN_VIBRANT_COLORS.length]
  );
};

const getBasinLinkColors = (sources = [], nodeColors = [], existing = []) => {
  return sources.map((source, index) => {
    const supplied = existing[index];
    if (typeof supplied === "string" && supplied.trim()) {
      // The backend may already provide carefully chosen flow colors.
      // Preserve them unless it is clearly an empty placeholder.
      return supplied;
    }

    return hexToRgba(nodeColors[Number(source)] || "#4d7fb8", 0.58);
  });
};

const transformBasinCoordinates = (coordinates) => {
  // Intentionally do not normalize, pad, shift, or zoom YAML coordinates.
  // The coordinates in wtt_node_coord.yml are the source of truth.
  return coordinates;
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatFlow = (value, unit = "") => {
  const number = toNumber(value);
  if (number === null) return null;

  return `${number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}${unit ? ` ${unit}` : ""}`;
};

const getPercent = (value, total) => {
  const number = toNumber(value);
  const totalNumber = toNumber(total);

  if (number === null || totalNumber === null || totalNumber === 0) {
    return null;
  }

  return (number / totalNumber) * 100;
};

const normalizeFlowBreakdown = (items, direction = "inflow") => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      const name =
        item.name ??
        item.country ??
        item.neighbor ??
        item.source ??
        item.from ??
        item.target ??
        item.to ??
        item?.label ??
        "Unknown";

      const value =
        item.value ??
        item.flow ??
        item.amount ??
        item.total ??
        item[direction] ??
        null;

      const percent =
        item.percent ??
        item.percentage ??
        item.share ??
        item.percent_of_total ??
        item.percent_total ??
        null;

      return {
        name,
        source: item.source,
        target: item.target,
        value: toNumber(value),
        percent: toNumber(percent),
      };
    })
    .filter((item) => item && (item.value !== null || item.percent !== null))
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
};

const buildFlowStats = (data) => {
  const summary =
    data.flow_summary ||
    data.summary ||
    data.totals ||
    data.stats ||
    data.flowStats ||
    null;

  const inflow =
    data.total_inflow ??
    data.inflow_total ??
    data.inflow ??
    data.totalInflow ??
    summary?.total_inflow ??
    summary?.inflow_total ??
    summary?.inflow ??
    summary?.totalInflow ??
    null;

  const outflow =
    data.total_outflow ??
    data.outflow_total ??
    data.outflow ??
    data.totalOutflow ??
    summary?.total_outflow ??
    summary?.outflow_total ??
    summary?.outflow ??
    summary?.totalOutflow ??
    null;

  const inflowBreakdown = normalizeFlowBreakdown(
    data.inflow_breakdown ??
      data.inflows ??
      data.inflow_sources ??
      data.sources ??
      summary?.inflow_breakdown ??
      summary?.inflows ??
      summary?.inflow_sources ??
      summary?.sources,
    "inflow"
  );

  const outflowBreakdown = normalizeFlowBreakdown(
    data.outflow_breakdown ??
      data.outflows ??
      data.outflow_targets ??
      data.targets ??
      summary?.outflow_breakdown ??
      summary?.outflows ??
      summary?.outflow_targets ??
      summary?.targets,
    "outflow"
  );

  return {
    inflow,
    outflow,
    inflowBreakdown,
    outflowBreakdown,
    links: Array.isArray(data.links) ? data.links : [],
    hasDynamicValues:
      inflow !== null ||
      outflow !== null ||
      inflowBreakdown.length > 0 ||
      outflowBreakdown.length > 0,
  };
};

const buildBasinFlowStats = (basinPlotData) => {
  if (!basinPlotData) return null;

  const sankey = basinPlotData.sankey || basinPlotData;

  const nodeLabels =
    Array.isArray(sankey?.node_label)
      ? sankey.node_label
      : Array.isArray(basinPlotData?.nodes?.label)
      ? basinPlotData.nodes.label
      : [];

  const sources =
    Array.isArray(sankey?.sources)
      ? sankey.sources
      : Array.isArray(basinPlotData?.links?.source)
      ? basinPlotData.links.source
      : [];

  const targets =
    Array.isArray(sankey?.targets)
      ? sankey.targets
      : Array.isArray(basinPlotData?.links?.target)
      ? basinPlotData.links.target
      : [];

  const values =
    Array.isArray(sankey?.values)
      ? sankey.values
      : Array.isArray(basinPlotData?.links?.value)
      ? basinPlotData.links.value
      : [];

  const linkLabels =
    Array.isArray(sankey?.links)
      ? sankey.links
      : Array.isArray(basinPlotData?.links?.label)
      ? basinPlotData.links.label
      : [];

  const percentOuts =
    Array.isArray(sankey?.percent_outs)
      ? sankey.percent_outs
      : Array.isArray(basinPlotData?.links?.percent_out)
      ? basinPlotData.links.percent_out
      : [];

  if (!values.length || !nodeLabels.length) {
    return null;
  }

  const links = values
    .map((value, index) => {
      const sourceIndex = sources[index];
      const targetIndex = targets[index];

      const source =
        nodeLabels[sourceIndex] !== undefined
          ? nodeLabels[sourceIndex]
          : "Unknown source";

      const target =
        nodeLabels[targetIndex] !== undefined
          ? nodeLabels[targetIndex]
          : "Unknown target";

      const numberValue = toNumber(value);

      if (numberValue === null) {
        return null;
      }

      return {
        name:
          linkLabels[index] ||
          `${source} → ${target}`,
        source,
        target,
        value: numberValue,
        percent: toNumber(percentOuts[index]),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);

  const totalFlow = links.reduce(
    (sum, item) => sum + item.value,
    0
  );

  const summary = basinPlotData?.summary || basinPlotData?.totals || {};
  const reportedInflow = toNumber(
    basinPlotData?.total_inflow ??
    basinPlotData?.totalInflow ??
    basinPlotData?.inflow ??
    summary?.total_inflow ??
    summary?.totalInflow ??
    summary?.inflow
  );
  const reportedOutflow = toNumber(
    basinPlotData?.total_outflow ??
    basinPlotData?.totalOutflow ??
    basinPlotData?.outflow ??
    summary?.total_outflow ??
    summary?.totalOutflow ??
    summary?.outflow
  );

  return {
    // Prefer explicit backend totals. A sum of every link is not a valid
    // substitute for both inflow and outflow because internal links may be
    // counted more than once in a basin network.
    inflow: reportedInflow ?? (links.length ? totalFlow : null),
    outflow: reportedOutflow,
    unit: basinPlotData?.meta?.flow_unit || basinPlotData?.flow_unit || "km³",
    timeUnit: basinPlotData?.meta?.time_unit || basinPlotData?.time_unit || "year",

    inflowBreakdown: links.map((item) => ({
      ...item,
      percent:
        item.percent ??
        getPercent(item.value, totalFlow),
    })),

    outflowBreakdown: [],

    links,

    hasDynamicValues: links.length > 0 || reportedInflow !== null || reportedOutflow !== null,
  };
};

// Read the actual Sankey trace first. Basin outflow is the value on a final
// ocean/sea-bound edge in the displayed diagram, never a sum of country flows.
const getBasinFinalOutflowInfo = (basinPlotData, diagramLinks = []) => {
  const terminalPattern = /\b(ocean|sea|atlantic|pacific|indian ocean|gulf of guinea|mediterranean|red sea|arabian sea|river mouth|mouth of the nile|ocean outlet|sea outlet)\b/i;
  const explicitOceanKeys = [
    "ocean_outflow", "outflow_to_ocean", "basin_outflow_to_ocean",
    "final_ocean_outflow", "ocean_discharge", "discharge_to_ocean",
    "outflow_to_sea", "final_outflow_to_ocean",
  ];
  const clean = (value) => String(value ?? "")
    .replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
  const sankey = basinPlotData?.sankey || basinPlotData || {};
  const nodeLabels = Array.isArray(sankey?.node_label)
    ? sankey.node_label
    : Array.isArray(basinPlotData?.nodes?.label) ? basinPlotData.nodes.label : [];
  const sources = Array.isArray(sankey?.sources)
    ? sankey.sources
    : Array.isArray(basinPlotData?.links?.source) ? basinPlotData.links.source : [];
  const targets = Array.isArray(sankey?.targets)
    ? sankey.targets
    : Array.isArray(basinPlotData?.links?.target) ? basinPlotData.links.target : [];
  const values = Array.isArray(sankey?.values)
    ? sankey.values
    : Array.isArray(basinPlotData?.links?.value) ? basinPlotData.links.value : [];
  const labels = Array.isArray(sankey?.links)
    ? sankey.links
    : Array.isArray(basinPlotData?.links?.label) ? basinPlotData.links.label : [];

  // Use the same node indices and flow values as the currently rendered Sankey.
  const plottedLinks = values.map((rawValue, index) => {
    const sourceIndex = Number(sources[index]);
    const targetIndex = Number(targets[index]);
    const source = clean(nodeLabels[sourceIndex]) || "Unknown source";
    const target = clean(nodeLabels[targetIndex]) || "Unknown target";
    const value = toNumber(rawValue);
    if (value === null || !Number.isInteger(sourceIndex) || !Number.isInteger(targetIndex)) return null;
    return {
      source, target, value,
      name: clean(labels[index]) || `${source} → ${target}`,
      sourceIndex, targetIndex,
    };
  }).filter(Boolean);

  const isExplicitOutlet = (link) => {
    const target = clean(link?.target);
    const label = clean(link?.name ?? link?.label);
    return terminalPattern.test(target) ||
      /\b(to|into|reaches?|discharge(?:s|d)?)\s+(the\s+)?(ocean|sea|atlantic|pacific|indian ocean)\b|\b(ocean|sea)\s+outlet\b/i.test(label);
  };

  const toDisplayLinks = (links) => links
    .filter((link) => toNumber(link?.value) !== null)
    .map((link) => ({ ...link, value: toNumber(link.value) }))
    .sort((a, b) => b.value - a.value);

  const explicitOutletLinks = toDisplayLinks(plottedLinks.filter(isExplicitOutlet));
  if (explicitOutletLinks.length) {
    return {
      links: explicitOutletLinks,
      value: explicitOutletLinks.length === 1 ? explicitOutletLinks[0].value : null,
      source: "displayed-sankey-ocean-outlet-link",
    };
  }

  // If the diagram has no explicit ocean node, identify sink links by node index,
  // not label text (labels can repeat). Each terminal pathway is kept separate.
  if (plottedLinks.length) {
    const sourceNodeIndices = new Set(plottedLinks.map((link) => link.sourceIndex));
    const sinkLinks = toDisplayLinks(plottedLinks.filter((link) =>
      !sourceNodeIndices.has(link.targetIndex) && link.targetIndex !== link.sourceIndex
    ));
    if (sinkLinks.length) {
      return {
        links: sinkLinks,
        value: sinkLinks.length === 1 ? sinkLinks[0].value : null,
        source: "displayed-sankey-terminal-link",
      };
    }
  }

  // If the raw trace shape differs, inspect the already-normalized diagram links.
  const normalizedLinks = toDisplayLinks(diagramLinks.map((link) => ({
    ...link,
    source: clean(link?.source),
    target: clean(link?.target),
    name: clean(link?.name ?? link?.label),
  })));
  const normalizedExplicit = normalizedLinks.filter(isExplicitOutlet);
  if (normalizedExplicit.length) {
    return {
      links: normalizedExplicit,
      value: normalizedExplicit.length === 1 ? normalizedExplicit[0].value : null,
      source: "normalized-sankey-ocean-outlet-link",
    };
  }

  // If only normalized links are available, identify their terminal targets by label.
  // Keep these values separate; never sum them into a synthetic basin total.
  if (normalizedLinks.length) {
    const normalizedSources = new Set(normalizedLinks.map((link) => clean(link.source)));
    const normalizedSinks = normalizedLinks.filter((link) =>
      clean(link.target) && !normalizedSources.has(clean(link.target)) && clean(link.target) !== clean(link.source)
    );
    if (normalizedSinks.length) {
      return {
        links: normalizedSinks,
        value: normalizedSinks.length === 1 ? normalizedSinks[0].value : null,
        source: "normalized-sankey-terminal-link",
      };
    }
  }

  // Explicit ocean-specific metadata is a last resort; generic totals are never used.
  const roots = [basinPlotData, basinPlotData?.meta, basinPlotData?.summary, basinPlotData?.totals];
  for (const root of roots) {
    if (!root || typeof root !== "object") continue;
    for (const key of explicitOceanKeys) {
      const value = toNumber(root[key]);
      if (value !== null) return { links: [], value, source: key };
    }
  }

  return { links: [], value: null, source: null };
};

const readBalancedJson = (text, startIndex) => {
  const opener = text[startIndex];
  const closer = opener === "[" ? "]" : "}";

  let depth = 0;
  let inString = false;
  let quote = "";
  let escaped = false;

  for (let i = startIndex; i < text.length; i += 1) {
    const char = text[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === quote) {
        inString = false;
      }
      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      quote = char;
      continue;
    }

    if (char === opener) depth += 1;
    if (char === closer) depth -= 1;

    if (depth === 0) {
      return text.slice(startIndex, i + 1);
    }
  }

  return null;
};

const extractWaterTrackerDataFromHtml = (html) => {
  const marker = "window.WATERTRACKER_SANKEY_DATA";
  const markerIndex = html.indexOf(marker);

  if (markerIndex === -1) return null;

  const equalsIndex = html.indexOf("=", markerIndex);
  if (equalsIndex === -1) return null;

  const objectStart = html.indexOf("{", equalsIndex);
  if (objectStart === -1) return null;

  const jsonText = readBalancedJson(html, objectStart);
  if (!jsonText) return null;

  try {
    return JSON.parse(jsonText);
  } catch (error) {
    console.warn("Failed to parse WATERTRACKER_SANKEY_DATA:", error);
    return null;
  }
};

const extractPlotlySankeyTraceFromHtml = (html) => {
  if (!html || typeof html !== "string") return null;

  let searchIndex = 0;

  while (searchIndex < html.length) {
    const plotIndex = html.indexOf("Plotly.newPlot", searchIndex);
    if (plotIndex === -1) break;

    const firstArray = html.indexOf("[", plotIndex);
    if (firstArray === -1) break;

    const arrayText = readBalancedJson(html, firstArray);

    if (arrayText) {
      try {
        const traces = JSON.parse(arrayText);
        const sankeyTrace = Array.isArray(traces)
          ? traces.find(
              (trace) =>
                trace?.type === "sankey" &&
                Array.isArray(trace?.link?.value) &&
                Array.isArray(trace?.node?.label)
            )
          : null;

        if (sankeyTrace) return sankeyTrace;
      } catch (error) {
        // Some Plotly HTML blocks may contain non-JSON JavaScript. Continue
        // searching for another Plotly.newPlot call instead of failing.
      }
    }

    searchIndex = plotIndex + "Plotly.newPlot".length;
  }

  return null;
};

const buildFlowStatsFromSankeyTrace = (trace, focusName = "") => {
  const labels = Array.isArray(trace?.node?.label) ? trace.node.label : [];
  const sources = Array.isArray(trace?.link?.source) ? trace.link.source : [];
  const targets = Array.isArray(trace?.link?.target) ? trace.link.target : [];
  const values = Array.isArray(trace?.link?.value) ? trace.link.value : [];
  const linkLabels = Array.isArray(trace?.link?.label) ? trace.link.label : [];

  if (!labels.length || !values.length) return null;

  const links = values
    .map((value, index) => {
      const numericValue = toNumber(value);
      const sourceIndex = sources[index];
      const targetIndex = targets[index];

      if (numericValue === null) return null;

      const source = labels[sourceIndex] ?? `Node ${sourceIndex}`;
      const target = labels[targetIndex] ?? `Node ${targetIndex}`;

      return {
        name: linkLabels[index] || `${source} → ${target}`,
        source: String(source),
        target: String(target),
        value: numericValue,
        percent: null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);

  if (!links.length) return null;

  const visibleTotal = links.reduce((sum, item) => sum + item.value, 0);
  const normalizedFocus = String(focusName || "").trim().toLowerCase();
  const focusLabel = normalizedFocus
    ? labels.find(
        (label) => String(label).trim().toLowerCase() === normalizedFocus
      )
    : null;

  const incoming = focusLabel
    ? links.filter((item) => item.target === String(focusLabel))
    : [];
  const outgoing = focusLabel
    ? links.filter((item) => item.source === String(focusLabel))
    : [];

  const incomingTotal = incoming.reduce((sum, item) => sum + item.value, 0);
  const outgoingTotal = outgoing.reduce((sum, item) => sum + item.value, 0);

  const denominatorIn = incomingTotal || visibleTotal;
  const denominatorOut = outgoingTotal || visibleTotal;

  const inflowBreakdown = (incoming.length ? incoming : links).map((item) => ({
    ...item,
    name: incoming.length ? item.source : item.name,
    percent: getPercent(item.value, denominatorIn),
  }));

  const outflowBreakdown = outgoing.map((item) => ({
    ...item,
    name: item.target,
    percent: getPercent(item.value, denominatorOut),
  }));

  return {
    inflow: incoming.length ? incomingTotal : visibleTotal,
    outflow: outgoing.length ? outgoingTotal : null,
    inflowBreakdown,
    outflowBreakdown,
    links: links.map((item) => ({
      ...item,
      percent: getPercent(item.value, visibleTotal),
    })),
    visibleTotal,
    focusLabel: focusLabel ? String(focusLabel) : null,
    hasDynamicValues: true,
    source: "plotly-diagram",
  };
};


const prepareCountrySankeyHtml = (
  html,
  flowStats = null,
  flowUnit = "km³",
  timeUnit = "year",
  focusName = ""
) => {
  if (!html || typeof html !== "string") return "";

  const inflowValue = toNumber(flowStats?.inflow);
  const outflowValue = toNumber(flowStats?.outflow);

  const inflowLabel =
    inflowValue !== null
      ? `${inflowValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${flowUnit}${timeUnit ? `/${timeUnit}` : ""}`
      : "";

  const outflowLabel =
    outflowValue !== null
      ? `${outflowValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${flowUnit}${timeUnit ? `/${timeUnit}` : ""}`
      : "";

  const safeFocusName = JSON.stringify(String(focusName || ""));

  const countryColorScript = `
    <script id="watertracker-country-colors">
      (function () {
        var focusName = ${safeFocusName};

        // Palette sampled from the reference image: soft, muted, high-contrast pastels.
        var referencePalette = [
          "#5FA9D6", // vibrant soft blue
          "#4FB7A5", // vibrant mint teal
          "#E5B94F", // vibrant warm sand
          "#9F83C4", // vibrant lavender
          "#78AD68", // vibrant sage
          "#D97862", // vibrant peach
          "#3F91C4", // deeper blue
          "#3FA394", // deeper teal
          "#D3942E", // deeper amber
          "#7655A8", // deeper violet
          "#5F994C", // deeper green
          "#B95E4C"  // deeper terracotta
        ];
        var focusColor = "#425978";

        function normalizeCountry(value) {
          return String(value == null ? "" : value)
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/&amp;/gi, "&")
            .replace(/<[^>]*>/g, "")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();
        }

        function countryKey(value) {
          var n = normalizeCountry(value);
          // Treat common display-only differences as the same country.
          n = n.replace(/[’']/g, "'");
          n = n.replace(/\s*[-–—]\s*/g, "-");
          return n;
        }

        var colorCache = Object.create(null);
        var nextColor = 0;

        function colorForCountry(label) {
          var key = countryKey(label);
          if (!key) return referencePalette[0];
          if (countryKey(focusName) === key) return focusColor;
          if (!colorCache[key]) {
            colorCache[key] = referencePalette[nextColor % referencePalette.length];
            nextColor += 1;
          }
          return colorCache[key];
        }

        function rgba(hex, alpha) {
          var h = String(hex).replace("#", "");
          var r = parseInt(h.slice(0,2), 16);
          var g = parseInt(h.slice(2,4), 16);
          var b = parseInt(h.slice(4,6), 16);
          return "rgba(" + r + "," + g + "," + b + "," + alpha + ")";
        }

        function applyReferenceColors(plot) {
          if (!plot || !plot.data || !plot.data.length || !window.Plotly) return;
          var trace = plot.data.find(function (d) { return d && d.type === "sankey"; });
          if (!trace || !trace.node || !trace.link) return;

          var labels = Array.isArray(trace.node.label) ? trace.node.label : [];
          var nodeColors = labels.map(function (label) {
            return colorForCountry(label);
          });

          var sources = Array.isArray(trace.link.source) ? trace.link.source : [];
          var targets = Array.isArray(trace.link.target) ? trace.link.target : [];
          var linkColors = sources.map(function (source, i) {
            var target = targets[i];
            var sourceLabel = labels[source];
            var targetLabel = labels[target];
            var sourceIsFocus = countryKey(sourceLabel) === countryKey(focusName);
            var targetIsFocus = countryKey(targetLabel) === countryKey(focusName);

            // For a country-to-focus or focus-to-country flow, use the
            // non-focus country's colour. This guarantees Kenya->focus and
            // focus->Kenya are identical while Kenya and Uganda stay different.
            if (sourceIsFocus && !targetIsFocus) return rgba(colorForCountry(targetLabel), 0.58);
            if (targetIsFocus && !sourceIsFocus) return rgba(colorForCountry(sourceLabel), 0.58);
            if (!sourceIsFocus && !targetIsFocus) return rgba(colorForCountry(sourceLabel), 0.48);
            return rgba(focusColor, 0.50);
          });

          try {
            window.Plotly.restyle(plot, {
              "node.color": [nodeColors],
              "link.color": [linkColors]
            }, [0]);
          } catch (error) {
            console.warn("Could not apply reference Sankey colours:", error);
          }
        }

        function run() {
          document.querySelectorAll(".plotly-graph-div, .js-plotly-plot").forEach(applyReferenceColors);
        }

        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", function () {
            setTimeout(run, 80);
            setTimeout(run, 350);
            setTimeout(run, 900);
          });
        } else {
          setTimeout(run, 80);
          setTimeout(run, 350);
          setTimeout(run, 900);
        }

        window.addEventListener("resize", function () { setTimeout(run, 80); });
      })();
    </script>
  `;

  const responsiveStyles = `
    <style id="watertracker-dashboard-responsive">
      html,
      body {
        width: 100% !important;
        min-width: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: hidden !important;
        background: #ffffff !important;
      }

      body {
        display: flex !important;
        justify-content: center !important;
        align-items: flex-start !important;
      }

      body > *,
      figure,
      .figure,
      .figure-container,
      .plotly-graph-div,
      .js-plotly-plot,
      .plot-container,
      .svg-container {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }

      figure,
      .figure,
      .figure-container,
      .plotly-graph-div,
      .js-plotly-plot,
      .plot-container {
        margin-left: auto !important;
        margin-right: auto !important;
      }

      figcaption {
        display: none !important;
      }

      /* Emphasize country names in the embedded Country Sankey. */
      .plotly .sankey .node text,
      .plotly g.sankey g.node text,
      .js-plotly-plot .sankey .node text {
        font-family: Inter, Arial, sans-serif !important;
        font-size: 15px !important;
        font-weight: 700 !important;
        fill: #173f7a !important;
      }

      .plotly .sankey .node rect,
      .js-plotly-plot .sankey .node rect {
        stroke: rgba(16, 32, 51, 0.38) !important;
        stroke-width: 1.15px !important;
      }

      /* The React dashboard already renders the Country title once.
         Hide only the duplicate Plotly title inside the generated Country HTML. */
      .plotly .g-gtitle,
      .plotly .g-gtitle text,
      .plotly .g-gtitle tspan {
        display: none !important;
      }
    </style>
  `;

  const safeInflowLabel = JSON.stringify(inflowLabel);
  const safeOutflowLabel = JSON.stringify(outflowLabel);

  const responsiveScript = `
    <script id="watertracker-dashboard-responsive-script">
      (function () {
        var totalInflowText = ${safeInflowLabel};
        var totalOutflowText = ${safeOutflowLabel};

        function removeFigureOne() {
          document
            .querySelectorAll("figcaption, p, span, div, small, label")
            .forEach(function (element) {
              if (element.childElementCount !== 0) return;

              var value = (element.textContent || "").trim();

              if (/^(fig(?:ure)?\\.?\\s*1)\\.?$/i.test(value)) {
                element.style.display = "none";
              }
            });
        }

        function annotationText(annotation) {
          if (!annotation) return "";
          return String(annotation.text || "")
            .replace(/<[^>]*>/g, "")
            .trim()
            .toLowerCase();
        }

        function addFlowTotals(plot) {
          if (!window.Plotly || !window.Plotly.relayout || !plot) return;

          var layout = plot.layout || {};
          var annotations = Array.isArray(layout.annotations)
            ? layout.annotations.slice()
            : [];

          // Remove only annotations inserted by this dashboard code.
          annotations = annotations.filter(function (annotation) {
            return !(
              annotation &&
              annotation.meta &&
              annotation.meta.watertrackerFlowTotal
            );
          });

          var inflowHeading = annotations.find(function (annotation) {
            return annotationText(annotation) === "inflow";
          });

          var outflowHeading = annotations.find(function (annotation) {
            return annotationText(annotation) === "outflow";
          });

          function buildTotalAnnotation(heading, text, fallbackX) {
            if (!text) return null;

            var x =
              heading && typeof heading.x === "number"
                ? heading.x
                : fallbackX;

            // Put the total on its own row ABOVE the heading and above the
            // Sankey ribbons. Paper coordinates increase upward, so add to y.
            var y =
              heading && typeof heading.y === "number"
                ? Math.min(0.985, heading.y + 0.055)
                : 0.965;

            return {
              x: x,
              y: y,
              xref: heading?.xref || "paper",
              yref: heading?.yref || "paper",
              text: "<b>" + text + "</b>",
              showarrow: false,
              xanchor: "center",
              yanchor: "bottom",
              align: "center",
              font: {
                size: 13,
                color: "#173f7a"
              },
              meta: {
                watertrackerFlowTotal: true
              }
            };
          }

          var inflowAnnotation = buildTotalAnnotation(
            inflowHeading,
            totalInflowText,
            0.31
          );

          var outflowAnnotation = buildTotalAnnotation(
            outflowHeading,
            totalOutflowText,
            0.69
          );

          if (inflowAnnotation) annotations.push(inflowAnnotation);
          if (outflowAnnotation) annotations.push(outflowAnnotation);

          try {
            window.Plotly.relayout(plot, {
              annotations: annotations,
              autosize: true,
              width: null,
              "margin.l": 22,
              "margin.r": 22,
              "margin.t": 58
            });
          } catch (error) {
            console.warn("Could not add Sankey flow totals:", error);
          }
        }

        function resizeSankey() {
          document
            .querySelectorAll(".plotly-graph-div, .js-plotly-plot")
            .forEach(function (plot) {
              plot.style.width = "100%";
              plot.style.maxWidth = "100%";
              plot.style.marginLeft = "auto";
              plot.style.marginRight = "auto";

              addFlowTotals(plot);

              // The original HTML often stores a fixed Plotly height (e.g. 500px).
              // In a comparison iframe this leaves a large blank area beneath the
              // diagram. Reflow the actual Plotly figure to the iframe viewport.
              if (window.Plotly && window.Plotly.relayout) {
                try {
                  var viewportWidth = Math.max(320, document.documentElement.clientWidth || window.innerWidth || 320);
                  var viewportHeight = Math.max(300, document.documentElement.clientHeight || window.innerHeight || 300);
                  var currentLayout = plot.layout || {};
                  var originalMargin = currentLayout.margin || {};
                  window.Plotly.relayout(plot, {
                    autosize: true,
                    width: viewportWidth,
                    height: viewportHeight,
                    "margin.l": Math.max(12, Math.min(Number(originalMargin.l) || 30, 42)),
                    "margin.r": Math.max(12, Math.min(Number(originalMargin.r) || 30, 42)),
                    "margin.t": Math.max(36, Math.min(Number(originalMargin.t) || 54, 66)),
                    "margin.b": Math.max(18, Math.min(Number(originalMargin.b) || 28, 42))
                  });
                } catch (error) {}
              }

              if (window.Plotly && window.Plotly.Plots) {
                try {
                  window.Plotly.Plots.resize(plot);
                } catch (error) {}
              }
            });
        }

        function run() {
          removeFigureOne();
          resizeSankey();
        }

        function delayedRun() {
          window.setTimeout(run, 80);
          window.setTimeout(run, 350);
          window.setTimeout(run, 900);
        }

        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", delayedRun);
        } else {
          delayedRun();
        }

        window.addEventListener("resize", function () {
          window.setTimeout(resizeSankey, 60);
        });
      })();
    </script>
  `;

  let updatedHtml = html.replace(
    /<figcaption[^>]*>\s*(?:Fig(?:ure)?\.?\s*1)\.?\s*<\/figcaption>/gi,
    ""
  );

  const comparisonFitStyles = `<style id="watertracker-comparison-fit">
    :root, html, body {
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      background: #ffffff !important;
      box-sizing: border-box !important;
    }
    body {
      display: block !important;
    }
    body > div, body > figure, figure, .figure, .figure-container,
    .plot-container, .svg-container, .plotly-graph-div, .js-plotly-plot {
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      height: 100% !important;
      min-height: 0 !important;
      max-height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
    }
    .main-svg { max-width: 100% !important; }
    .modebar-container, .modebar { display: none !important; }
  </style>`;

  if (updatedHtml.includes("</head>")) {
    updatedHtml = updatedHtml.replace(
      "</head>",
      `${responsiveStyles}${comparisonFitStyles}</head>`
    );
  } else {
    updatedHtml = `${responsiveStyles}${updatedHtml}`;
  }

  if (updatedHtml.includes("</body>")) {
    updatedHtml = updatedHtml.replace(
      "</body>",
      `${countryColorScript}${responsiveScript}</body>`
    );
  } else {
    updatedHtml = `${updatedHtml}${countryColorScript}${responsiveScript}`;
  }

  return updatedHtml;
};

const buildCountryFlowStatsFromHtml = async (htmlUrl, focusName = "") => {
  try {
    const response = await fetch(htmlUrl, { cache: "no-cache" });
    if (!response.ok) {
      throw new Error(`Unable to read Sankey HTML (${response.status})`);
    }

    const html = await response.text();

    // Preferred source: explicit data object injected by the backend.
    const embeddedData = extractWaterTrackerDataFromHtml(html);
    if (embeddedData) {
      const stats = buildFlowStats(embeddedData);
      if (stats?.hasDynamicValues) {
        return { ...stats, source: "watertracker-data" };
      }
    }

    // Robust fallback: read the same Plotly trace that draws the Sankey.
    const sankeyTrace = extractPlotlySankeyTraceFromHtml(html);
    if (sankeyTrace) {
      return buildFlowStatsFromSankeyTrace(sankeyTrace, focusName);
    }

    return null;
  } catch (error) {
    console.warn("Could not read country Sankey HTML:", error);
    return null;
  }
};

const buildFlowListText = (items = [], unit = "", type = "inflow") => {
  if (!items.length) return "";

  return items
    .slice(0, 8)
    .map((item) => {
      const valueText = formatFlow(item.value, unit) || "an unavailable value";

      const percentText =
        item.percent !== null && item.percent !== undefined
          ? `${item.percent.toFixed(2)} percent`
          : "an unavailable percentage";

      if (type === "inflow") {
        return `${item.name} contributes ${valueText}, representing ${percentText} of total inflow`;
      }

      return `${item.name} receives ${valueText}, representing ${percentText} of total outflow`;
    })
    .join(". ");
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f7fa",
    padding: "10px 12px 18px",
    fontFamily: "Inter, Arial, sans-serif",
    color: palette.text,
  },
  shell: {
    width: "100%",
    maxWidth: "none",
    margin: "0 auto",
  },
  dashboardHeader: {
    display: "grid",
    boxSizing: "border-box",
    minHeight: 76,
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
    padding: "10px 16px",
    marginBottom: 8,
    borderRadius: 16,
    background: "#12324a",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 4px 14px rgba(8, 26, 44, 0.10)",

    // Keep the application header visible while the dashboard scrolls.
    position: "sticky",
    top: 8,
    zIndex: 15000,
    isolation: "isolate",
    backdropFilter: "blur(14px)",
    transition: "padding 180ms ease, border-radius 180ms ease, box-shadow 180ms ease, background 180ms ease",
  },
  headerBrand: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  },
  logoGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  logo: {
    maxHeight: 38,
    maxWidth: 108,
    objectFit: "contain",
    filter: "brightness(0) invert(1)",
  },
  logoDivider: {
    width: 1,
    height: 36,
    background: "rgba(255,255,255,0.28)",
  },
  titleBlock: {
    minWidth: 0,
    textAlign: "center",
    justifySelf: "center",
  },
  appTitle: {
    margin: 0,
    color: "#ffffff",
    fontSize: 21,
    fontWeight: 800,
    letterSpacing: "-0.035em",
    lineHeight: 1.08,
  },
  appSubtitle: {
    margin: "5px 0 0",
    color: "rgba(255,255,255,0.74)",
    fontSize: 13,
    lineHeight: 1.4,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    minWidth: 300,
  },
  homeButton: {
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 999,
    background: "rgba(255,255,255,0.1)",
    color: "#ffffff",
    padding: "11px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  statusPill: {
    borderRadius: 999,
    background: "rgba(70,187,212,0.16)",
    color: "#dff8ff",
    padding: "11px 14px",
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid rgba(70,187,212,0.24)",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(250px, 270px) minmax(0, 1fr)",
    gap: 14,
    alignItems: "start",
  },
  card: {
    background: palette.panel,
    border: `1px solid ${palette.border}`,
    borderRadius: 16,
    boxShadow: "0 3px 12px rgba(8,26,44,0.055)",
    backdropFilter: "none",
  },
  sidebar: {
    padding: 13,
    position: "sticky",
    top: 12,
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  main: {
    padding: "6px 10px 10px",
    minWidth: 0,
    width: "100%",
    minHeight: "calc(100vh - 112px)",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  sectionHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 800,
    margin: 0,
  },
  sectionText: {
    margin: 0,
    color: palette.textSoft,
    fontSize: 13,
    lineHeight: 1.45,
  },
  radioPanel: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    padding: 10,
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: `1px solid ${palette.border}`,
  },
  radioOption: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "11px 12px",
    borderRadius: 14,
    border: `1px solid ${palette.borderStrong}`,
    background: "#ffffff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
    color: palette.primary,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 750,
    color: "#31485d",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 14,
    border: `1px solid ${palette.borderStrong}`,
    background: "rgba(255,255,255,0.98)",
    fontSize: 14,
    color: palette.text,
    outline: "none",
    boxSizing: "border-box",
    boxShadow: palette.innerShadow,
  },
  compactInput: {
    minWidth: 120,
    padding: "9px 10px",
    borderRadius: 10,
    border: `1px solid ${palette.borderStrong}`,
    background: "rgba(255,255,255,0.98)",
    fontSize: 13,
    color: palette.text,
    outline: "none",
    boxSizing: "border-box",
  },
  fieldsPanel: {
    display: "grid",
    gap: 10,
    padding: 14,
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: `1px solid ${palette.border}`,
  },
  gridTwo: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  button: {
    width: "100%",
    padding: "12px 14px",
    border: "none",
    borderRadius: 14,
    background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.accent} 100%)`,
    color: "#ffffff",
    fontWeight: 800,
    fontSize: 13,
    cursor: "pointer",
    boxShadow: "0 12px 22px rgba(2, 151, 166, 0.20)",
  },
  secondaryButton: {
    width: "100%",
    padding: "12px 14px",
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 14,
    background: "#ffffff",
    color: palette.primary,
    fontWeight: 800,
    fontSize: 13,
    cursor: "pointer",
    boxShadow: palette.shadowSoft,
  },
  buttonDisabled: {
    opacity: 0.72,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  error: {
    padding: "12px 14px",
    background: palette.dangerBg,
    border: `1px solid ${palette.dangerBorder}`,
    color: palette.dangerText,
    borderRadius: 14,
    fontSize: 13,
    lineHeight: 1.5,
  },
  visualTop: {
    display: "grid",
    gap: 12,
    padding: 16,
    borderRadius: 22,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.98) 100%)",
    border: `1px solid ${palette.border}`,
    boxShadow: "0 12px 34px rgba(8, 26, 44, 0.05)",
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  heading: {
    margin: 0,
    fontSize: 22,
    fontWeight: 850,
    letterSpacing: "-0.03em",
  },
  subheading: {
    margin: "4px 0 0",
    color: palette.textSoft,
    fontSize: 13,
    lineHeight: 1.45,
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(150px, 1fr))",
    gap: 12,
  },
  metaCard: {
    padding: "12px 14px",
    borderRadius: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%)",
    border: `1px solid ${palette.border}`,
    boxShadow: palette.shadowSoft,
  },
  metaTitle: {
    margin: 0,
    fontSize: 10,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 750,
  },
  metaValue: {
    margin: "4px 0 0",
    fontSize: 13,
    fontWeight: 700,
    color: palette.text,
    lineHeight: 1.3,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: palette.panelSolid,
    border: `1px solid ${palette.border}`,
    color: palette.textSoft,
    fontSize: 12,
    fontWeight: 750,
  },
  basinTabs: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 8,
    borderRadius: 16,
    background: "rgba(232,244,248,0.72)",
    border: `1px solid ${palette.border}`,
    width: "fit-content",
  },
  basinTabButton: {
    border: "none",
    borderRadius: 12,
    padding: "10px 16px",
    background: "transparent",
    color: palette.textSoft,
    fontSize: 13,
    fontWeight: 850,
    cursor: "pointer",
  },
  basinTabButtonActive: {
    background: "#ffffff",
    color: palette.primary,
    boxShadow: palette.shadowSoft,
  },
  animationLoading: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: palette.textSoft,
    fontSize: 14,
    fontWeight: 750,
    background:
      "linear-gradient(180deg, rgba(255,255,255,1), rgba(232,244,248,0.72))",
  },
  animationError: {
    margin: 16,
    padding: "12px 14px",
    borderRadius: 14,
    background: palette.dangerBg,
    border: `1px solid ${palette.dangerBorder}`,
    color: palette.dangerText,
    fontSize: 13,
    lineHeight: 1.5,
  },
  frameWrap: {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    borderRadius: 12,
    overflow: "hidden",
    background: palette.panelSolid,
    border: "1px solid #e2e8ef",
    boxShadow: "0 2px 8px rgba(16,32,51,0.035)",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    background: "white",
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    borderRadius: 24,
    border: `1px dashed ${palette.borderStrong}`,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(232,244,248,0.84) 100%)",
    color: palette.textSoft,
    padding: 24,
  },
  emptyBadge: {
    width: 78,
    height: 78,
    borderRadius: 24,
    background: `linear-gradient(135deg, ${palette.primarySoft} 0%, #ffffff 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
    marginBottom: 18,
    border: `1px solid ${palette.borderStrong}`,
    boxShadow: palette.shadowSoft,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 850,
    color: palette.text,
  },
  emptyText: {
    margin: "8px 0 0",
    maxWidth: 440,
    lineHeight: 1.65,
    fontSize: 14,
  },
  aiAgent: {
    display: "grid",
    gap: 14,
    padding: 18,
    borderRadius: 24,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(241,250,253,0.98) 100%)",
    border: `1px solid ${palette.borderStrong}`,
    boxShadow: "0 16px 42px rgba(8, 26, 44, 0.08)",
  },
  aiAgentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  aiIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  aiAvatar: {
    width: 48,
    height: 48,
    borderRadius: 16,
    background: `linear-gradient(135deg, ${palette.teal}, ${palette.accent})`,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    boxShadow: "0 12px 26px rgba(2,151,166,0.24)",
  },
  aiTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    color: palette.text,
  },
  aiSubtitle: {
    margin: "4px 0 0",
    color: palette.textSoft,
    fontSize: 13,
    lineHeight: 1.45,
  },
  aiActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  aiButton: {
    border: "none",
    borderRadius: 14,
    padding: "11px 15px",
    background: `linear-gradient(135deg, ${palette.teal}, ${palette.accent})`,
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(2,151,166,0.20)",
  },
  aiStopButton: {
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 14,
    padding: "11px 15px",
    background: "#ffffff",
    color: palette.primary,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  aiStatusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(160px, 1fr))",
    gap: 10,
  },
  aiStatusCard: {
    padding: 13,
    borderRadius: 16,
    background: "#ffffff",
    border: `1px solid ${palette.border}`,
    boxShadow: palette.shadowSoft,
  },
  aiStatusTitle: {
    margin: 0,
    fontSize: 10,
    fontWeight: 900,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  aiStatusValue: {
    margin: "5px 0 0",
    fontSize: 14,
    fontWeight: 900,
    color: palette.primary,
    lineHeight: 1.35,
  },
  aiNarrationBox: {
    display: "grid",
    gap: 8,
    padding: 15,
    borderRadius: 18,
    background: "#ffffff",
    border: `1px solid ${palette.border}`,
    boxShadow: palette.shadowSoft,
  },
  aiNarrationTitle: {
    margin: 0,
    fontSize: 12,
    fontWeight: 900,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  aiNarrationText: {
    margin: 0,
    color: palette.text,
    fontSize: 15,
    lineHeight: 1.65,
    fontWeight: 700,
  },
  animationWorkspace: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 310px",
    gap: 16,
    alignItems: "stretch",
    width: "100%",
  },
  animationMapPanel: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    borderRadius: 22,
    overflow: "hidden",
    background: "#ffffff",
    border: `1px solid ${palette.border}`,
    boxShadow: palette.shadowSoft,
  },
  animationMapHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "13px 16px",
    borderBottom: `1px solid ${palette.border}`,
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(247,251,252,1) 100%)",
  },
  animationMapTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 900,
    color: palette.text,
  },
  animationMapSubtitle: {
    margin: "4px 0 0",
    fontSize: 12,
    color: palette.textSoft,
    lineHeight: 1.45,
  },
  animationMapStage: {
    height: 720,
    minHeight: 720,
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    overflow: "hidden",
    background: "#ffffff",
  },
  animationIframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
    background: "#ffffff",
  },
  animationSidePanel: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 14,
    borderRadius: 22,
    background:
      "linear-gradient(180deg, rgba(241,250,253,0.98) 0%, rgba(255,255,255,0.98) 100%)",
    border: `1px solid ${palette.borderStrong}`,
    boxShadow: palette.shadowSoft,
  },
  animationInfoCard: {
    padding: 13,
    borderRadius: 16,
    background: "#ffffff",
    border: `1px solid ${palette.border}`,
  },
  animationInfoLabel: {
    margin: 0,
    fontSize: 10,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 900,
  },
  animationInfoValue: {
    margin: "5px 0 0",
    color: palette.primary,
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.4,
  },
  animationNote: {
    margin: 0,
    color: palette.textSoft,
    fontSize: 12,
    lineHeight: 1.55,
  },
  animationActionButton: {
    width: "100%",
    border: "none",
    borderRadius: 14,
    padding: "11px 13px",
    background: `linear-gradient(135deg, ${palette.teal}, ${palette.accent})`,
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },


  riverViewModeTabs: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: 5,
    borderRadius: 14,
    background: "#eef6f8",
    border: `1px solid ${palette.border}`,
    width: "fit-content",
  },
  riverViewModeTab: {
    border: "none",
    borderRadius: 10,
    padding: "9px 16px",
    background: "transparent",
    color: palette.textSoft,
    fontSize: 13,
    fontWeight: 850,
    cursor: "pointer",
  },
  riverViewModeTabActive: {
    background: "#ffffff",
    color: palette.primary,
    boxShadow: palette.shadowSoft,
  },
  riverAnimationControlsCard: {
    display: "grid",
    gap: 12,
    padding: 13,
    borderRadius: 17,
    background: "#ffffff",
    border: `1px solid ${palette.border}`,
    boxShadow: palette.shadowSoft,
  },
  riverAnimationOptions: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },
  riverAnimationOptionButton: {
    minHeight: 44,
    padding: "9px 10px",
    borderRadius: 12,
    border: `1px solid ${palette.borderStrong}`,
    background: "#ffffff",
    color: palette.textSoft,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "center",
    lineHeight: 1.3,
  },
  riverAnimationOptionButtonActive: {
    background: palette.primarySoft,
    color: palette.primary,
    border: `1px solid ${palette.teal}`,
    boxShadow: "0 8px 18px rgba(2,151,166,0.12)",
  },

  riverModeSelectWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  riverModeSelectLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  riverModeSelect: {
    minWidth: 245,
    padding: "9px 34px 9px 11px",
    borderRadius: 10,
    border: `1px solid ${palette.borderStrong}`,
    background: "#ffffff",
    color: palette.text,
    fontSize: 12,
    fontWeight: 800,
    outline: "none",
    cursor: "pointer",
  },

  riverPlaybackRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  riverPlaybackButtons: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  riverPlaybackButton: {
    minHeight: 38,
    padding: "8px 13px",
    borderRadius: 10,
    border: `1px solid ${palette.borderStrong}`,
    background: "#ffffff",
    color: palette.primary,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
  },
  riverPlaybackButtonPrimary: {
    background: palette.primary,
    color: "#ffffff",
    border: `1px solid ${palette.primary}`,
  },
  riverPeriodChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 38,
    padding: "8px 13px",
    borderRadius: 10,
    background: "#f8fbfc",
    border: `1px solid ${palette.border}`,
    color: palette.primary,
    fontSize: 12,
    fontWeight: 900,
  },
  riverCompareGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  riverComparePanel: {
    minWidth: 0,
    borderRadius: 17,
    overflow: "hidden",
    border: `1px solid ${palette.border}`,
    background: "#ffffff",
    boxShadow: palette.shadowSoft,
  },
  riverCompareHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    borderBottom: `1px solid ${palette.border}`,
    background: "#f8fbfc",
  },
  riverCompareTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 900,
    color: palette.text,
  },
  mapExtentButton: {
    display: "none",
  },

  riverAnimationModeCard: {
    display: "grid",
    gap: 10,
    padding: 13,
    marginBottom: 12,
    borderRadius: 17,
    background: "linear-gradient(180deg, #ffffff 0%, #f4fbfd 100%)",
    border: `1px solid ${palette.borderStrong}`,
    boxShadow: palette.shadowSoft,
  },
  riverAnimationModeLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 900,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
  },
  riverAnimationModeValue: {
    margin: 0,
    color: palette.primary,
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.35,
  },
  riverAnimationModeHelp: {
    margin: 0,
    color: palette.textSoft,
    fontSize: 12,
    lineHeight: 1.5,
  },

  animationSecondaryAction: {
    width: "100%",
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 14,
    padding: "11px 13px",
    background: "#ffffff",
    color: palette.primary,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  flowAnalysisButtonPanel: {
    minHeight: 220,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 14,
    padding: 18,
    borderRadius: 20,
    border: `1px solid ${palette.borderStrong}`,
    background:
      "linear-gradient(145deg, rgba(239,251,253,0.98) 0%, rgba(255,255,255,0.98) 72%)",
    boxShadow: palette.shadowSoft,
  },
  flowAnalysisButtonIcon: {
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    background: `linear-gradient(135deg, ${palette.teal}, ${palette.accent})`,
    color: "#ffffff",
    fontSize: 23,
    boxShadow: "0 12px 24px rgba(2,151,166,0.22)",
  },
  flowAnalysisButtonEyebrow: {
    margin: 0,
    color: palette.textMuted,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  flowAnalysisButtonTitle: {
    margin: "5px 0 0",
    color: palette.text,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.25,
  },
  flowAnalysisButtonText: {
    margin: 0,
    color: palette.textSoft,
    fontSize: 12,
    lineHeight: 1.55,
  },
  flowAnalysisPrimaryButton: {
    width: "100%",
    minHeight: 50,
    border: "none",
    borderRadius: 15,
    background: `linear-gradient(135deg, ${palette.teal}, ${palette.accent})`,
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(2,151,166,0.22)",
  },
  flowAnalysisPrimaryButtonDisabled: {
    opacity: 0.58,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  flowAnalysisBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 12000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    background: "rgba(7,26,47,0.54)",
    backdropFilter: "blur(5px)",
  },
  flowAnalysisModal: {
    width: "min(820px, 96vw)",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 24,
    border: `1px solid ${palette.borderStrong}`,
    background: "#ffffff",
    boxShadow: "0 30px 80px rgba(8,26,44,0.30)",
  },
  flowAnalysisModalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "17px 20px",
    borderBottom: `1px solid ${palette.border}`,
    background:
      "linear-gradient(135deg, rgba(236,249,252,1) 0%, rgba(255,255,255,1) 76%)",
  },
  flowAnalysisModalIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  flowAnalysisModalIcon: {
    width: 46,
    height: 46,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 15,
    background: `linear-gradient(135deg, ${palette.teal}, ${palette.accent})`,
    color: "#ffffff",
    fontSize: 22,
  },
  flowAnalysisModalTitle: {
    margin: 0,
    color: palette.text,
    fontSize: 19,
    fontWeight: 900,
  },
  flowAnalysisModalSubtitle: {
    margin: "4px 0 0",
    color: palette.textSoft,
    fontSize: 12,
    lineHeight: 1.4,
  },
  flowAnalysisCloseButton: {
    width: 38,
    height: 38,
    flex: "0 0 auto",
    border: `1px solid ${palette.border}`,
    borderRadius: 12,
    background: "#ffffff",
    color: palette.primary,
    fontSize: 23,
    lineHeight: 1,
    cursor: "pointer",
  },
  flowAnalysisModalBody: {
    overflowY: "auto",
    padding: 20,
  },
  flowAnalysisMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  flowAnalysisMetaCard: {
    padding: "12px 13px",
    borderRadius: 14,
    border: `1px solid ${palette.border}`,
    background: "#f8fbfc",
  },
  flowAnalysisMetaLabel: {
    margin: 0,
    color: palette.textMuted,
    fontSize: 9,
    fontWeight: 900,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
  },
  flowAnalysisMetaValue: {
    margin: "5px 0 0",
    color: palette.primary,
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.35,
  },
  flowAnalysisSummaryBox: {
    padding: 18,
    borderRadius: 17,
    border: `1px solid ${palette.border}`,
    background:
      "linear-gradient(180deg, #ffffff 0%, #f8fcfd 100%)",
    boxShadow: palette.shadowSoft,
  },
  flowAnalysisSummaryLabel: {
    margin: 0,
    color: palette.textMuted,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
  },
  flowAnalysisSummaryText: {
    margin: "10px 0 0",
    color: palette.text,
    fontSize: 14,
    fontWeight: 650,
    lineHeight: 1.75,
  },
  flowAnalysisModalFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    padding: "14px 20px",
    borderTop: `1px solid ${palette.border}`,
    background: "#f8fbfc",
  },
  flowAnalysisDoneButton: {
    minHeight: 40,
    padding: "0 18px",
    border: "none",
    borderRadius: 12,
    background: palette.teal,
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(7,26,47,0.48)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: 10,
    boxSizing: "border-box",
  },
  modal: {
    width: "min(98vw, 1680px)",
    height: "min(97dvh, 1100px)",
    maxHeight: "97dvh",
    maxWidth: 1680,
    minHeight: 0,
    background: palette.panelSolid,
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${palette.border}`,
    boxShadow: "0 30px 80px rgba(8,26,44,0.28)",
    display: "grid",
    gridTemplateRows: "auto auto minmax(0, 1fr)",
    boxSizing: "border-box",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 16px",
    borderBottom: `1px solid ${palette.border}`,
    background: "#ffffff",
    position: "relative",
    zIndex: 2,
    minWidth: 0,
    flexShrink: 0,
  },
  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 850,
    color: palette.text,
  },
  modalText: {
    margin: "4px 0 0",
    color: palette.textSoft,
    fontSize: 12,
    lineHeight: 1.35,
  },
  modalControls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderBottom: `1px solid ${palette.border}`,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,251,252,0.95) 100%)",
    overflowY: "auto",
    maxHeight: 150,
  },
  controlGroupLabel: {
    fontSize: 11,
    fontWeight: 850,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginRight: 2,
  },
  compareGraphArea: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    padding: 8,
    overflow: "hidden",
    minHeight: 0,
    height: "100%",
    boxSizing: "border-box",
    background:
      "linear-gradient(180deg, rgba(248,251,252,0.9) 0%, rgba(255,255,255,0.95) 100%)",
  },
  compareCard: {
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
    border: `1px solid ${palette.border}`,
    borderTop: "3px solid #0297a6",
    borderRadius: 14,
    boxShadow: "0 5px 18px rgba(16, 52, 75, 0.07)",
    overflow: "hidden",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
  },
  compareCaption: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 850,
    color: palette.text,
    padding: "12px 14px",
    borderBottom: `1px solid ${palette.border}`,
    background: "linear-gradient(90deg, #f2fbfc 0%, #ffffff 72%)",
    minHeight: 42,
    lineHeight: 1.35,
    flexShrink: 0,
  },
  compareValues: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px 14px",
    alignItems: "center",
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 800,
    color: palette.text,
    background: "#f5fafb",
    borderBottom: `1px solid ${palette.border}`,
    flexShrink: 0,
  },
  compareFrameWrap: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    flex: "1 1 auto",
    borderTop: `1px solid ${palette.border}`,
    background: "#ffffff",
    overflow: "hidden",
    flex: "1 1 0%",
    height: "auto",
  },
  compareEmpty: {
    width: "100%",
    borderTop: `1px solid ${palette.border}`,
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(232,244,248,0.74) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: palette.textSoft,
    fontSize: 14,
    fontWeight: 700,
  },
};

export default function SankeyPanelwater({ onBack }) {
  const [selectionType, setSelectionType] = useState("country");
  // Keep the header and Sankey viewport dimensions stable while scrolling.
  // Scroll-triggered header resizing was causing the chart window to redraw/flicker.
  const headerCompact = false;

  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState("");

  const [country, setCountry] = useState("");
  const [basin, setBasin] = useState("");
  const [basins, setBasins] = useState([]);
  const [basinsLoading, setBasinsLoading] = useState(true);
  const [basinsError, setBasinsError] = useState("");
  const [basinMeta, setBasinMeta] = useState(null);
  const [basinYears, setBasinYears] = useState([]);

  const [timeStep, setTimeStep] = useState("LTMA");
  const [year, setYear] = useState("2024");
  const [month, setMonth] = useState("1");
  const [height, setHeight] = useState("520");

  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1440 : window.innerWidth
  );

  const [loading, setLoading] = useState(false);
  // Auto-generate only once per view for the initial experience. Subsequent
  // changes to country, basin, year, month, or time step require the user to
  // press the existing Generate button.
  const autoGenerateStartedRef = useRef({ country: false, basin: false });
  const [error, setError] = useState("");
  const [htmlUrl, setHtmlUrl] = useState("");
  const [countrySankeyHtml, setCountrySankeyHtml] = useState("");
  const [basinPlotData, setBasinPlotData] = useState(null);
  const basinCanvasRef = useRef(null);
  const [basinCanvasWidth, setBasinCanvasWidth] = useState(0);
  const [basinViewTab, setBasinViewTab] = useState("sankey");
  const [basinZoom, setBasinZoom] = useState(1.04);
  const [basinAnimationUrl, setBasinAnimationUrl] = useState("");
  const [basinAnimationLoading, setBasinAnimationLoading] = useState(false);
  const [basinAnimationError, setBasinAnimationError] = useState("");
  const [basinAnimationData, setBasinAnimationData] = useState(null);
  const [basinAnimationHtml, setBasinAnimationHtml] = useState("");
  const [riverViewMode, setRiverViewMode] = useState("animation");
  const [riverAnimationMode, setRiverAnimationMode] = useState(
    "fixed-year-months"
  );
  const [riverAnimationIndex, setRiverAnimationIndex] = useState(0);
  const [riverAnimationPlaying, setRiverAnimationPlaying] = useState(false);
  const [riverCompareLeftIndex, setRiverCompareLeftIndex] = useState(0);
  const [riverCompareRightIndex, setRiverCompareRightIndex] = useState(1);
  const [riverCompareLeftUrl, setRiverCompareLeftUrl] = useState("");
  const [riverCompareRightUrl, setRiverCompareRightUrl] = useState("");
  const [riverCompareLeftHtml, setRiverCompareLeftHtml] = useState("");
  const [riverCompareRightHtml, setRiverCompareRightHtml] = useState("");
  const [riverCompareLoading, setRiverCompareLoading] = useState(false);
  const [riverCompareError, setRiverCompareError] = useState("");
  const riverAnimationPlayTimerRef = useRef(null);
  const riverAnimationRequestRef = useRef(0);
  const riverComparisonRequestRef = useRef(0);
  const basinAnimationFrameRef = useRef(null);
  const [flowAnalysisOpen, setFlowAnalysisOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [flowStats, setFlowStats] = useState(null);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAiAgentRunning, setIsAiAgentRunning] = useState(false);
  const aiTimerRef = useRef(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [animationLoading, setAnimationLoading] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const [animationLabel, setAnimationLabel] = useState("");
  // Keep animation-frame controls separate from the user's static selections.
  const [animationFrameYear, setAnimationFrameYear] = useState("");
  const [animationFrameMonth, setAnimationFrameMonth] = useState("");
  const [gifExporting, setGifExporting] = useState(false);
  const [gifExportProgress, setGifExportProgress] = useState("");
  const animationTimerRef = useRef(null);

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState("");

  const [compareCountry, setCompareCountry] = useState("");
  const [compareBasin, setCompareBasin] = useState("");
  const [compareTimeStep, setCompareTimeStep] = useState("Annual");
  const [compareYearA, setCompareYearA] = useState("2023");
  const [compareYearB, setCompareYearB] = useState("2024");
  const [compareMonth, setCompareMonth] = useState("1");
  const [compareMonthA, setCompareMonthA] = useState("1");
  const [compareMonthB, setCompareMonthB] = useState("1");
  const [compareLeftBasinPlotData, setCompareLeftBasinPlotData] = useState(null);
  const [compareRightBasinPlotData, setCompareRightBasinPlotData] = useState(null);
  const [compareLeftFlowStats, setCompareLeftFlowStats] = useState(null);
  const [compareRightFlowStats, setCompareRightFlowStats] = useState(null);

  const [compareLeftUrl, setCompareLeftUrl] = useState("");
  const [compareRightUrl, setCompareRightUrl] = useState("");
  const [compareLeftHtml, setCompareLeftHtml] = useState("");
  const [compareRightHtml, setCompareRightHtml] = useState("");


  
  useEffect(() => {
    const element = basinCanvasRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const nextWidth = Math.round(rect.width);

      if (nextWidth > 0) {
        setBasinCanvasWidth((previous) =>
          previous === nextWidth ? previous : nextWidth
        );
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, [selectionType, basinViewTab, basinPlotData]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        setCountriesError("");

        const response = await fetch(COUNTRIES_API_URL, {
          signal: controller.signal,
          cache: "no-cache",
        });

        const raw = await response.text();
        let data = [];

        try {
          data = raw ? JSON.parse(raw) : [];
        } catch {
          throw new Error(`Countries API returned invalid JSON: ${raw}`);
        }

        if (!response.ok) {
          throw new Error(
            data?.detail || "Failed to load countries from the backend"
          );
        }

        const normalizedCountries = Array.isArray(data)
          ? data
              .map((item) =>
                typeof item === "string"
                  ? item.trim()
                  : String(
                      item?.country ??
                        item?.name ??
                        item?.label ??
                        ""
                    ).trim()
              )
              .filter(Boolean)
          : [];

        const uniqueCountries = [...new Set(normalizedCountries)].sort((a, b) =>
          a.localeCompare(b)
        );

        if (!uniqueCountries.length) {
          throw new Error("Countries API returned an empty country list");
        }

        if (!isMounted) return;

        setCountries(uniqueCountries);

        setCountry((currentCountry) =>
          uniqueCountries.includes(currentCountry)
            ? currentCountry
            : uniqueCountries.includes("Ethiopia")
            ? "Ethiopia"
            : uniqueCountries[0]
        );

        setCompareCountry((currentCountry) =>
          uniqueCountries.includes(currentCountry)
            ? currentCountry
            : uniqueCountries.includes("Ethiopia")
            ? "Ethiopia"
            : uniqueCountries[0]
        );
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!isMounted) return;

        setCountries([]);
        setCountriesError(
          err.message || "Unable to load countries from the backend"
        );
      } finally {
        if (isMounted) {
          setCountriesLoading(false);
        }
      }
    };

    loadCountries();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Generic basin discovery. Existing country and animation APIs remain unchanged.
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadBasins = async () => {
      try {
        setBasinsLoading(true);
        setBasinsError("");

        const response = await fetch(
          "/api/wtt/basins",
          { signal: controller.signal, cache: "no-cache" }
        );

        const raw = await response.text();
        let data = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          throw new Error(`Basins API returned invalid JSON: ${raw}`);
        }

        if (!response.ok) {
          throw new Error(data?.detail || "Failed to load basins from the backend");
        }

        const discovered = Array.isArray(data?.basins)
          ? data.basins
              .map((item) =>
                typeof item === "string"
                  ? { id: item, name: item.replace(/_/g, " ") }
                  : {
                      id: String(item?.id ?? item?.name ?? "").trim(),
                      name: String(item?.name ?? item?.id ?? "").trim(),
                    }
              )
              .filter((item) => item.id)
          : [];

        const uniqueBasins = [
          ...new Map(
            discovered.map((item) => [item.id.toLowerCase(), item])
          ).values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        if (!uniqueBasins.length) {
          throw new Error("Basins API returned an empty basin list");
        }

        if (!isMounted) return;

        setBasins(uniqueBasins);
        setBasin((current) => {
          const match = uniqueBasins.find(
            (item) => item.id.toLowerCase() === String(current).toLowerCase()
          );
          return match?.id || uniqueBasins[0].id;
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!isMounted) return;
        setBasins([]);
        setBasinsError(err.message || "Unable to load basins from the backend");
      } finally {
        if (isMounted) setBasinsLoading(false);
      }
    };

    loadBasins();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Discover the available years/time steps for the selected basin.
  useEffect(() => {
    if (!basin) {
      setBasinMeta(null);
      setBasinYears([]);
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadBasinMeta = async () => {
      try {
        const response = await fetch(
          `/api/wtt/basins/${encodeURIComponent(basin)}/meta`,
          { signal: controller.signal, cache: "no-cache" }
        );

        const raw = await response.text();
        let data = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          throw new Error(`Basin metadata API returned invalid JSON: ${raw}`);
        }

        if (!response.ok) {
          throw new Error(
            data?.detail || `Failed to load metadata for ${basin}`
          );
        }

        const years = Array.isArray(data?.years)
          ? data.years
              .map((item) => String(item))
              .filter((item) => /^\d{4}$/.test(item))
              .sort((a, b) => Number(b) - Number(a))
          : [];

        if (!isMounted) return;

        setBasinMeta(data);
        setBasinYears(years);

        if (years.length) {
          setYear((current) =>
            years.includes(String(current))
              ? String(current)
              : String(data.default_year ?? years[0])
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!isMounted) return;
        setBasinMeta(null);
        setBasinYears([]);
        setError(err.message || `Unable to load metadata for ${basin}`);
      }
    };

    loadBasinMeta();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [basin]);

  useEffect(() => {
    setBasinViewTab("sankey");
    setBasinAnimationUrl("");
    setBasinAnimationHtml("");
    setBasinAnimationError("");
    setBasinAnimationData(null);
    setRiverAnimationPlaying(false);
    setFlowAnalysisOpen(false);
  }, [selectionType, basin]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const stopAiAgent = () => {
    if (aiTimerRef.current) {
      clearTimeout(aiTimerRef.current);
      aiTimerRef.current = null;
    }

    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setIsSpeaking(false);
    setIsAiAgentRunning(false);
  };

  useEffect(() => {
    return () => {
      stopAiAgent();
    };
  }, []);

  const isSmallScreen = viewportWidth < 768;
  const isMediumScreen = viewportWidth >= 768 && viewportWidth < 1200;

  const availableTimeSteps =
    selectionType === "basin"
      ? TIME_STEPS.filter((item) =>
          Array.isArray(basinMeta?.time_steps)
            ? basinMeta.time_steps.includes(item.value)
            : true
        )
      : TIME_STEPS;

  const showYear = timeStep === "Annual" || timeStep === "Monthly";
  const showMonth = timeStep === "LTMM" || timeStep === "Monthly";
  // Annual animates across years; LTMM animates across climatological months.
  // Monthly animates across all calendar months within the selected year.
  const showAnimationButton =
    timeStep === "Annual" || timeStep === "Monthly" || timeStep === "LTMM";

  const showCompareYear =
    compareTimeStep === "Annual" || compareTimeStep === "Monthly";

  const showCompareMonth =
    compareTimeStep === "LTMM" || compareTimeStep === "Monthly";

  const selectedName = selectionType === "country" ? country : basin;

  const chartHeightOptions = isSmallScreen
    ? [
        { value: 420, label: "420px — Small screens / laptops" },
        { value: 480, label: "480px — Compact dashboard view" },
        { value: 520, label: "520px — Recommended for normal screens" },
        { value: 600, label: "600px — Taller mobile/tablet view" },
      ]
    : isMediumScreen
    ? [
        { value: 520, label: "520px — Normal screens" },
        { value: 600, label: "600px — Comfortable dashboard view" },
        { value: 650, label: "650px — Large laptop screens" },
        { value: 720, label: "720px — Expanded view" },
      ]
    : [
        { value: 520, label: "520px — Normal desktop" },
        { value: 600, label: "600px — Recommended desktop view" },
        { value: 650, label: "650px — Large screen" },
        { value: 720, label: "720px — Wide monitor" },
        { value: 800, label: "800px — Extra large display" },
      ];

  const countryFrameHeight = `${Math.max(
    Number(height),
    isSmallScreen ? 480 : isMediumScreen ? 580 : 640
  )}px`;

  const frameHeight = countryFrameHeight;

  const compareHeight = isSmallScreen ? "420px" : "520px";

  const goHome = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    window.location.href = "/";
  };

  const getTimeStepLabel = (value) =>
    TIME_STEPS.find((item) => item.value === value)?.label || value;

  const getMonthLabel = (monthValue) =>
    MONTHS.find((item) => String(item.value) === String(monthValue))?.label ||
    monthValue;

  const buildPayload = ({
    selectedSelectionType = selectionType,
    selectedCountry = country,
    selectedBasin = basin,
    selectedTimeStep = timeStep,
    selectedYear = year,
    selectedMonth = month,
    selectedHeight = height,
  }) => {
   const safeTimeStep = selectedTimeStep;

    const safeYear = String(selectedYear);

    const base = {
      scope: selectedSelectionType,
      time_step: safeTimeStep,
      month: null,
      // Country keeps its existing user-selected height. Basin rendering is
      // fully responsive in React and does not depend on the chart-height UI.
      height:
        selectedSelectionType === "country"
          ? Number(selectedHeight)
          : 600,
    };

    if (selectedSelectionType === "country") {
      base.country = selectedCountry;
    }

    if (selectedSelectionType === "basin") {
      base.basin = selectedBasin;
    }

    if (safeTimeStep === "LTMA") {
      return { ...base, time: "LTMA" };
    }

    if (safeTimeStep === "LTMM") {
      return { ...base, time: null, month: Number(selectedMonth) };
    }

    if (safeTimeStep === "Annual") {
      return { ...base, time: safeYear };
    }

    if (safeTimeStep === "Monthly") {
      return {
        ...base,
        time: `${safeYear}-${String(selectedMonth).padStart(2, "0")}`,
      };
    }

    return base;
  };

  const payload = useMemo(
    () => buildPayload({}),
    [selectionType, country, basin, timeStep, year, month, height]
  );

  const fetchSankey = async (customPayload) => {
    if (customPayload.scope === "basin") {
      const params = new URLSearchParams();

      params.append("time_step", customPayload.time_step);

      if (
        (customPayload.time_step === "Annual" ||
          customPayload.time_step === "Monthly") &&
        customPayload.time
      ) {
        params.append("time", customPayload.time);
      }

      if (
        customPayload.time_step === "LTMM" &&
        customPayload.month != null
      ) {
        params.append("month", String(customPayload.month));
      }

      const response = await fetch(
        `/api/wtt/basins/${encodeURIComponent(
          customPayload.basin
        )}/sankey?${params.toString()}`,
        {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
        }
      );

      const raw = await response.text();
      let data = {};

      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(`Server returned non-JSON response: ${raw}`);
      }

      if (!response.ok) {
        throw new Error(
          data.detail || "Failed to generate basin Sankey diagram"
        );
      }

      const sankey =
        data &&
        typeof data.sankey === "object" &&
        data.sankey !== null
          ? data.sankey
          : {};

      // Prefer the explicit JSON arrays returned by general_basin.
      const nodeLabel = Array.isArray(data.node_label)
        ? data.node_label
        : Array.isArray(sankey.node_label)
          ? sankey.node_label
          : [];

      const nodeColor = Array.isArray(data.node_color)
        ? data.node_color
        : Array.isArray(sankey.node_color)
          ? sankey.node_color
          : [];

      const nodeHover = Array.isArray(data.node_hover_text)
        ? data.node_hover_text
        : Array.isArray(sankey.node_hover_text)
          ? sankey.node_hover_text
          : [];

      const sources = Array.isArray(data.sources)
        ? data.sources
        : Array.isArray(sankey.sources)
          ? sankey.sources
          : [];

      const targets = Array.isArray(data.targets)
        ? data.targets
        : Array.isArray(sankey.targets)
          ? sankey.targets
          : [];

      const values = Array.isArray(data.values)
        ? data.values
        : Array.isArray(sankey.values)
          ? sankey.values
          : [];

      const linkLabels = Array.isArray(data.links)
        ? data.links
        : Array.isArray(sankey.links)
          ? sankey.links
          : [];

      const linkColors = Array.isArray(data.link_colors)
        ? data.link_colors
        : Array.isArray(sankey.link_colors)
          ? sankey.link_colors
          : [];

      const percentIns = Array.isArray(data.percent_ins)
        ? data.percent_ins
        : Array.isArray(sankey.percent_ins)
          ? sankey.percent_ins
          : [];

      const percentOuts = Array.isArray(data.percent_outs)
        ? data.percent_outs
        : Array.isArray(sankey.percent_outs)
          ? sankey.percent_outs
          : [];

      // Coordinates are passed through untouched.
      const coordinates =
        data?.coordinates ||
        sankey?.coordinates ||
        null;

      const basinName =
        data?.basin ||
        customPayload.basin;

      const timePeriod =
        data?.time_period ||
        data?.time ||
        customPayload.time ||
        "";

      const flowUnit =
        data?.flow_unit ||
        "km³";

      const timeUnit =
        data?.time_unit ||
        "year";

      const normalizedBasinPlotData = {
        ...data,

        nodes: {
          label: nodeLabel,
          color: nodeColor,
          hover: nodeHover,
          customdata: nodeHover,
        },

        links: {
          source: sources,
          target: targets,
          value: values,
          label: linkLabels,
          color: linkColors,
          customdata: percentOuts,
          percent_in: percentIns,
          percent_out: percentOuts,
        },

        // Keep the exact backend coordinate arrays available to the renderer.
        coordinates,

        meta: {
          basin: basinName,
          time: data?.time ?? null,
          timePeriod,
          flow_unit: flowUnit,
          time_unit: timeUnit,
          title: `${String(
            basinName || "Basin"
          ).replaceAll("_", " ")} — ${
            timePeriod || "Selected period"
          }`,
          coordinates,
        },
      };

      return {
        htmlUrl: null,
        basinPlotData: normalizedBasinPlotData,
        meta: {
          basin: basinName,
          timePeriod,
          flowUnit,
          timeUnit,
          coordinates,
        },
        flowStats: buildBasinFlowStats(
          normalizedBasinPlotData
        ),
      };
    }

    const response = await fetch("/api/sankey/country", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(customPayload),
    });

    const raw = await response.text();
    let data = {};

    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      throw new Error(`Server returned non-JSON response: ${raw}`);
    }

    if (!response.ok) {
      throw new Error(data.detail || "Failed to generate Sankey diagram");
    }

    if (!data.html_url) {
      throw new Error("Backend did not return html_url");
    }

    //const fullHtmlUrl = `http://127.0.0.1:8000${data.html_url}`
    const fullHtmlUrl = data.html_url;
    const countryMeta = {
      country: data.country,
      basin: data.basin,
      timePeriod: data.time_period,
      flowUnit: data.flow_unit,
      timeUnit: data.time_unit,
    };

    let generatedHtml = "";
    let extractedFlowStats = null;

    try {
      const htmlResponse = await fetch(fullHtmlUrl, { cache: "no-cache" });

      if (htmlResponse.ok) {
        generatedHtml = await htmlResponse.text();

        const embeddedData = extractWaterTrackerDataFromHtml(generatedHtml);

        if (embeddedData) {
          extractedFlowStats = buildFlowStats(embeddedData);
        }

        if (!extractedFlowStats?.hasDynamicValues) {
          const sankeyTrace = extractPlotlySankeyTraceFromHtml(generatedHtml);

          if (sankeyTrace) {
            extractedFlowStats = buildFlowStatsFromSankeyTrace(
              sankeyTrace,
              data.country || customPayload.country
            );
          }
        }
      }
    } catch (htmlError) {
      console.warn("Could not prepare country Sankey HTML:", htmlError);
    }

    return {
      htmlUrl: fullHtmlUrl,
      htmlDoc: generatedHtml
        ? prepareCountrySankeyHtml(
            generatedHtml,
            extractedFlowStats ||
              (await buildCountryFlowStatsFromHtml(
                fullHtmlUrl,
                data.country || customPayload.country
              )) ||
              buildFlowStats(data),
            countryMeta.flowUnit || "km³",
            countryMeta.timeUnit || "year",
            data.country || customPayload.country || ""
          )
        : "",
      basinPlotData: null,
      meta: countryMeta,
      flowStats:
        extractedFlowStats ||
        (await buildCountryFlowStatsFromHtml(
          fullHtmlUrl,
          data.country || customPayload.country
        )) ||
        buildFlowStats(data),
    };
  };


  const riverAnimationPeriods = useMemo(() => {
    const yearsAscending = [...basinYears].reverse();

    if (riverAnimationMode === "fixed-month-years") {
      const fixedMonth = String(month).padStart(2, "0");

      return yearsAscending.map((itemYear) => ({
        year: itemYear,
        month: fixedMonth,
        start: `${itemYear}_${fixedMonth}`,
        end: `${itemYear}_${fixedMonth}`,
        label: `${getMonthLabel(fixedMonth)} ${itemYear}`,
        backendMode: "fixed_month_across_years",
        aggregation: "monthly",
      }));
    }

    if (riverAnimationMode === "annual-years") {
      return yearsAscending.map((itemYear) => ({
        year: itemYear,
        month: null,
        start: `${itemYear}_01`,
        end: `${itemYear}_12`,
        label: `${itemYear} annual`,
        backendMode: "annual_across_years",
        aggregation: "annual",
      }));
    }

    const fixedYear = year;

    return MONTHS.map((itemMonth) => {
      const itemMonthValue = String(itemMonth.value).padStart(2, "0");

      return {
        year: fixedYear,
        month: itemMonthValue,
        start: `${fixedYear}_${itemMonthValue}`,
        end: `${fixedYear}_${itemMonthValue}`,
        label: `${itemMonth?.label} ${fixedYear}`,
        backendMode: "fixed_year_across_months",
        aggregation: "monthly",
      };
    });
  }, [riverAnimationMode, year, month, basinYears]);

  const riverAnimationCurrentPeriod =
    riverAnimationPeriods[
      Math.min(
        Math.max(riverAnimationIndex, 0),
        Math.max(riverAnimationPeriods.length - 1, 0)
      )
    ] || null;

  const riverAnimationModeInfo =
    RIVER_ANIMATION_MODES.find(
      (item) => item.value === riverAnimationMode
    ) || RIVER_ANIMATION_MODES[1];

  const resetRiverAnimationIndex = (nextMode = riverAnimationMode) => {
    if (nextMode === "fixed-year-months") {
      const monthIndex = Math.max(0, Number(month) - 1);
      setRiverAnimationIndex(monthIndex);
      return;
    }

    const yearsAscending = [...basinYears].reverse();
    const yearIndex = yearsAscending.indexOf(String(year));
    setRiverAnimationIndex(yearIndex >= 0 ? yearIndex : 0);
  };

  const stepRiverAnimation = (direction) => {
    if (!riverAnimationPeriods.length || basinAnimationLoading) return;

    setBasinAnimationHtml("");

    setRiverAnimationIndex((current) => {
      const next =
        (current + direction + riverAnimationPeriods.length) %
        riverAnimationPeriods.length;

      return next;
    });
  };

  const toggleRiverAnimationPlayback = () => {
    if (basinAnimationLoading) return;
    setRiverAnimationPlaying((current) => !current);
  };


  const fetchRiverPeriodView = async ({
    period,
    mode = riverAnimationMode,
    force = true,
    hideGeneratedPlayback = true,
  }) => {
    if (!period) {
      throw new Error("No river-flow period was selected.");
    }

    // A unique token is important because the backend may reuse the same
    // generated HTML path for different dates.
    const requestToken = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;

    const params = new URLSearchParams({
      basin,
      start: period.start,
      end: period.end,
      interval: "900",
      force: String(force),

      animation_mode: period.backendMode || mode,
      aggregation: period.aggregation || "monthly",
      selected_year: String(period.year || year),

      // Cache-busting fields. Older backends can safely ignore these.
      request_id: requestToken,
      cache_bust: requestToken,
    });

    if (period.month) {
      params.set(
        "selected_month",
        String(period.month).padStart(2, "0")
      );
    }

    const response = await fetch(
      `${BASIN_ANIMATION_API_URL}?${params.toString()}`,
      {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      }
    );

    const raw = await response.text();
    let data = {};

    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      throw new Error(`Animation API returned invalid JSON: ${raw}`);
    }

    if (!response.ok) {
      throw new Error(
        data.detail || data.error || "Failed to load basin animation"
      );
    }

    if (!data.html_url) {
      throw new Error("Animation API did not return html_url");
    }

   // const absoluteUrl = data.html_url.startsWith("http")
     // ? data.html_url
      //: `http://127.0.0.1:8000${data.html_url}`;
      const absoluteUrl = data.html_url;


    const htmlSeparator = absoluteUrl.includes("?") ? "&" : "?";
    const uncachedHtmlUrl =
      `${absoluteUrl}${htmlSeparator}` +
      `period=${encodeURIComponent(period.start)}` +
      `&period_end=${encodeURIComponent(period.end)}` +
      `&request_id=${encodeURIComponent(requestToken)}`;

    // CRITICAL:
    // Read the generated HTML immediately. Do not leave the iframe pointing
    // to a shared backend HTML path because the next generation may overwrite it.
    const htmlResponse = await fetch(uncachedHtmlUrl, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
    });

    if (!htmlResponse.ok) {
      throw new Error(
        `Generated river map could not be read for ${period?.label || "selected period"}.`
      );
    }

    const rawHtml = await htmlResponse.text();

    if (!rawHtml || !rawHtml.trim()) {
      throw new Error(
        `Generated river map was empty for ${period?.label || "selected period"}.`
      );
    }

    const riverData =
      extractRiverAnimationData(rawHtml);

    const enhancedHtml = enhanceRiverAnimationHtml(rawHtml, {
      periodLabel: period?.label || "selected period",
      hideGeneratedPlayback,
    });

    return {
      url: uncachedHtmlUrl,
      html: enhancedHtml,
      rawHtml,
      data,
      riverData,
      period,
      requestToken,
    };
  };

  const loadBasinAnimation = async ({
    force = true,
    period = riverAnimationCurrentPeriod,
    mode = riverAnimationMode,
  } = {}) => {
    if (selectionType !== "basin") return;
    if (!period) return;

    const requestNumber = riverAnimationRequestRef.current + 1;
    riverAnimationRequestRef.current = requestNumber;

    setBasinAnimationLoading(true);
    setBasinAnimationError("");
    setBasinAnimationHtml("");

    try {
      const result = await fetchRiverPeriodView({
        period,
        mode,
        force,
        hideGeneratedPlayback: true,
      });

      // If the user already moved to another month/year, ignore this old result.
      if (requestNumber !== riverAnimationRequestRef.current) {
        return;
      }

      // Prefer srcDoc. This freezes the exact values generated for this period,
      // even if the backend later overwrites the same physical HTML file.
      setBasinAnimationUrl(result.url);
      setBasinAnimationHtml(result.html);

      setBasinAnimationData({
        ...result.data,
        basin,
        year: period.year,
        month: period.month,
        start: period.start,
        end: period.end,
        timeStep:
          period.aggregation === "annual" ? "Annual" : "Monthly",
        animationMode: mode,
        periodLabel: period?.label || "selected period",
        aggregation: period.aggregation,
        requestToken: result.requestToken,
        riverData: result.riverData,
      });
    } catch (err) {
      if (requestNumber !== riverAnimationRequestRef.current) {
        return;
      }

      setBasinAnimationError(
        err.message || "Unable to load the animated basin flow map"
      );
    } finally {
      if (requestNumber === riverAnimationRequestRef.current) {
        setBasinAnimationLoading(false);
      }
    }
  };

  const loadRiverComparison = async ({
    leftIndex = riverCompareLeftIndex,
    rightIndex = riverCompareRightIndex,
    force = true,
  } = {}) => {
    const leftPeriod = riverAnimationPeriods[leftIndex];
    const rightPeriod = riverAnimationPeriods[rightIndex];

    if (!leftPeriod || !rightPeriod) return;

    const requestNumber = riverComparisonRequestRef.current + 1;
    riverComparisonRequestRef.current = requestNumber;

    setRiverCompareLoading(true);
    setRiverCompareError("");
    setRiverCompareLeftHtml("");
    setRiverCompareRightHtml("");
    setRiverCompareLeftUrl("");
    setRiverCompareRightUrl("");

    try {
      /*
       * Generate A completely and capture its HTML BEFORE generating B.
       *
       * Do not use Promise.all here. If the backend writes every request to the
       * same output HTML path, simultaneous generation makes both frames read
       * the last-written values.
       */
      const leftResult = await fetchRiverPeriodView({
        period: leftPeriod,
        mode: riverAnimationMode,
        force,
        hideGeneratedPlayback: true,
      });

      if (requestNumber !== riverComparisonRequestRef.current) {
        return;
      }

      // Freeze A immediately in React state.
      setRiverCompareLeftUrl(leftResult.url);
      setRiverCompareLeftHtml(leftResult.html);

      const rightResult = await fetchRiverPeriodView({
        period: rightPeriod,
        mode: riverAnimationMode,
        force,
        hideGeneratedPlayback: true,
      });

      if (requestNumber !== riverComparisonRequestRef.current) {
        return;
      }

      setRiverCompareRightUrl(rightResult.url);
      setRiverCompareRightHtml(rightResult.html);
    } catch (err) {
      if (requestNumber !== riverComparisonRequestRef.current) {
        return;
      }

      setRiverCompareError(
        err.message || "Unable to load river comparison"
      );
    } finally {
      if (requestNumber === riverComparisonRequestRef.current) {
        setRiverCompareLoading(false);
      }
    }
  };

  const handleBasinViewTab = async (tabName) => {
    setBasinViewTab(tabName);

    if (tabName !== "animation") {
      setRiverAnimationPlaying(false);
      return;
    }

    resetRiverAnimationIndex(riverAnimationMode);

    if (!basinAnimationUrl && !basinAnimationLoading) {
      await loadBasinAnimation({
        period: riverAnimationCurrentPeriod,
        mode: riverAnimationMode,
      });
    }
  };

  useEffect(() => {
    if (
      selectionType !== "basin" ||
      basinViewTab !== "animation" ||
      !basinPlotData ||
      !riverAnimationCurrentPeriod
    ) {
      return;
    }

    const refreshTimer = window.setTimeout(() => {
      loadBasinAnimation({
        force: true,
        period: riverAnimationCurrentPeriod,
        mode: riverAnimationMode,
      });
    }, 180);

    return () => {
      window.clearTimeout(refreshTimer);
    };
  }, [
    selectionType,
    basin,
    basinViewTab,
    basinPlotData,
    riverAnimationMode,
    riverAnimationIndex,
  ]);

  useEffect(() => {
    if (
      !riverAnimationPlaying ||
      basinViewTab !== "animation" ||
      basinAnimationLoading ||
      !riverAnimationPeriods.length
    ) {
      return;
    }

    riverAnimationPlayTimerRef.current = window.setTimeout(() => {
      stepRiverAnimation(1);
    }, 2600);

    return () => {
      if (riverAnimationPlayTimerRef.current) {
        window.clearTimeout(riverAnimationPlayTimerRef.current);
        riverAnimationPlayTimerRef.current = null;
      }
    };
  }, [
    riverAnimationPlaying,
    riverAnimationIndex,
    basinAnimationLoading,
    basinViewTab,
    riverAnimationPeriods.length,
  ]);


  useEffect(() => {
    if (
      selectionType !== "basin" ||
      basinViewTab !== "animation" ||
      riverViewMode !== "comparison" ||
      !riverAnimationPeriods.length
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      loadRiverComparison({
        leftIndex: riverCompareLeftIndex,
        rightIndex: riverCompareRightIndex,
        force: true,
      });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [
    selectionType,
    basin,
    basinViewTab,
    riverViewMode,
    riverAnimationMode,
    riverCompareLeftIndex,
    riverCompareRightIndex,
  ]);

  useEffect(() => {
    const handleRiverAnimationMessage = (event) => {
      const message = event?.data;

      if (
        !message ||
        message.source !== "watertracker-river-animation"
      ) {
        return;
      }

      if (message.type === "previous") {
        setRiverAnimationPlaying(false);
        stepRiverAnimation(-1);
        return;
      }

      if (message.type === "next") {
        setRiverAnimationPlaying(false);
        stepRiverAnimation(1);
        return;
      }

      if (message.type === "toggle-play") {
        toggleRiverAnimationPlayback();
        return;
      }
    };

    window.addEventListener("message", handleRiverAnimationMessage);

    return () => {
      window.removeEventListener(
        "message",
        handleRiverAnimationMessage
      );
    };
  }, [
    riverAnimationMode,
    riverAnimationPeriods.length,
    year,
    month,
  ]);

  const stopAnimation = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    setIsAnimating(false);
    setAnimationLoading(false);
    setAnimationStep(0);
    setAnimationLabel("");
    setAnimationFrameYear("");
    setAnimationFrameMonth("");
  };

  const handleGenerate = async () => {
    if (selectionType === "country" && !country) {
      setError("Please wait for the country list to load or select a country.");
      return;
    }

    stopAnimation();
    stopAiAgent();
    setLoading(true);
    setError("");
    setHtmlUrl("");
    setCountrySankeyHtml("");
    setBasinPlotData(null);
    setFlowStats(null);
    setBasinViewTab("sankey");
    setBasinAnimationUrl("");
    setBasinAnimationHtml("");
    setBasinAnimationError("");
    setBasinAnimationData(null);
    setRiverAnimationPlaying(false);

    try {
      const result = await fetchSankey(payload);

      setHtmlUrl(result.htmlUrl);
      setCountrySankeyHtml(result.htmlDoc || "");
      setBasinPlotData(result.basinPlotData);
      setMeta(result.meta);
      setFlowStats(result.flowStats);
    } catch (err) {
      setError(err.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRef = useRef(null);
  handleGenerateRef.current = handleGenerate;

  // Automatically generate the active view once its own default selection is
  // ready. This supports both Country and Basin without changing the existing
  // payload builder, backend calls, or Sankey calculations.
  useEffect(() => {
    if (loading) return;

    if (selectionType === "country") {
      if (countriesLoading || !countries.length || !country) return;
    } else {
      if (basinsLoading || !basins.length || !basin) return;
    }

    if (autoGenerateStartedRef.current[selectionType]) return;

    autoGenerateStartedRef.current[selectionType] = true;
    handleGenerateRef.current?.();
  }, [
    selectionType,
    countriesLoading,
    basinsLoading,
    countries.length,
    basins.length,
    country,
    basin,
    timeStep,
    year,
    month,
    loading,
  ]);

  const handleStartAnimation = () => {
    if (!showAnimationButton) return;

    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    stopAiAgent();
    setError("");
    setAnimationLabel("");
    setAnimationFrameYear("");
    setAnimationFrameMonth("");
    setAnimationStep(0);
    setIsAnimating(true);
  };

  const waitForAnimationFrame = (ms = 700) =>
    new Promise((resolve) => window.setTimeout(resolve, ms));

  const loadExternalScript = (src, globalName) =>
    new Promise((resolve, reject) => {
      if (window[globalName]) {
        resolve(window[globalName]);
        return;
      }
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(window[globalName]), { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => window[globalName] ? resolve(window[globalName]) : reject(new Error(`${globalName} did not load`));
      script.onerror = () => reject(new Error("Could not load GIF export library. Check your internet connection."));
      document.head.appendChild(script);
    });

  const getVisiblePlotlyChart = () => {
    // Basin Sankey is rendered directly in React.
    const basinPlot = basinCanvasRef.current?.querySelector(".js-plotly-plot, .plotly-graph-div");
    if (basinPlot && basinPlot.data) {
      return { plot: basinPlot, plotly: window.Plotly };
    }

    // Country Sankey is rendered inside the generated HTML iframe.
    const iframe = document.querySelector('iframe[title="WaterTracker Sankey Diagram"]');
    try {
      const frameWindow = iframe?.contentWindow;
      const frameDocument = iframe?.contentDocument;
      const countryPlot = frameDocument?.querySelector(".js-plotly-plot, .plotly-graph-div");
      if (countryPlot && frameWindow?.Plotly) {
        return { plot: countryPlot, plotly: frameWindow.Plotly };
      }
    } catch (error) {
      // If a hosted country HTML is cross-origin, the browser correctly blocks
      // access; the user gets a clear message instead of a broken GIF.
    }
    return null;
  };

  const handleDownloadAnnualGif = async () => {
    if (timeStep !== "Annual" || gifExporting) return;

    const years = (selectionType === "basin" ? basinYears : YEARS)
      .map((value) => String(value))
      .filter((value) => /^\d{4}$/.test(value))
      .sort((a, b) => Number(a) - Number(b));

    if (!years.length) {
      setError("No annual years are available to export.");
      return;
    }

    const originalYear = year;
    const wasAnimating = isAnimating;
    stopAnimation();
    setGifExporting(true);
    setGifExportProgress("Preparing GIF…");
    setError("");

    let gif;
    try {
      const GIF = await loadExternalScript(
        "https://cdnjs.cloudflare.com/ajax/libs/gif.js/0.2.0/gif.js",
        "GIF"
      );
      // The GIF encoder's worker must be loaded from the same CDN.
      gif = new GIF({
        workers: 2,
        quality: 10,
        workerScript: "https://cdnjs.cloudflare.com/ajax/libs/gif.js/0.2.0/gif.worker.js",
        repeat: 0,
        background: "#ffffff",
      });

      for (let index = 0; index < years.length; index += 1) {
        const activeYear = years[index];
        setGifExportProgress(`Rendering ${activeYear} (${index + 1}/${years.length})…`);
        setYear(activeYear);

        const result = await fetchSankey(
          buildPayload({ selectedYear: activeYear })
        );
        setHtmlUrl(result.htmlUrl);
        setCountrySankeyHtml(result.htmlDoc || "");
        setBasinPlotData(result.basinPlotData);
        setMeta(result.meta);
        setFlowStats(result.flowStats);

        // Allow React, Plotly and the country iframe to finish rendering.
        await waitForAnimationFrame(1100);
        const visible = getVisiblePlotlyChart();
        if (!visible?.plot || !visible?.plotly?.toImage) {
          throw new Error(
            "Could not capture the rendered Sankey chart. Generate the diagram first and ensure it is visible before exporting."
          );
        }

        // Render a consistent frame, then place the Sankey at 82% size with
        // comfortable margins so it does not dominate the exported GIF.
        const imageUrl = await visible.plotly.toImage(visible.plot, {
          format: "png",
          width: 1100,
          height: 620,
          scale: 1,
        });

        const image = new Image();
        image.src = imageUrl;
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = () => reject(new Error(`Could not create the ${activeYear} GIF frame.`));
        });

        const canvas = document.createElement("canvas");
        canvas.width = 1280;
        canvas.height = 720;
        const context = canvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        const fitScale = Math.min((canvas.width * 0.82) / image.width, (canvas.height * 0.82) / image.height);
        const drawWidth = image.width * fitScale;
        const drawHeight = image.height * fitScale;
        context.drawImage(image, (canvas.width - drawWidth) / 2, (canvas.height - drawHeight) / 2, drawWidth, drawHeight);
        gif.addFrame(canvas, { delay: 900, copy: true });
      }

      setGifExportProgress("Encoding GIF…");
      const blob = await new Promise((resolve, reject) => {
        gif.on("finished", resolve);
        gif.on("abort", () => reject(new Error("GIF export was cancelled.")));
        gif.on("error", (error) => reject(error instanceof Error ? error : new Error("GIF encoding failed.")));
        gif.render();
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${String(selectedName || "sankey").replace(/[^a-z0-9_-]+/gi, "_")}_annual_sankey.gif`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      setGifExportProgress("GIF downloaded.");
    } catch (exportError) {
      setError(exportError?.message || "Unable to export annual GIF.");
      setGifExportProgress("");
    } finally {
      setYear(originalYear);
      try {
        const result = await fetchSankey(buildPayload({ selectedYear: originalYear }));
        setHtmlUrl(result.htmlUrl);
        setCountrySankeyHtml(result.htmlDoc || "");
        setBasinPlotData(result.basinPlotData);
        setMeta(result.meta);
        setFlowStats(result.flowStats);
      } catch (restoreError) {
        // Keep the last successfully rendered frame if restoring fails.
      }
      setGifExporting(false);
      if (wasAnimating) setIsAnimating(false);
    }
  };

  const handleDownloadAnnualVideo = async () => {
    if (gifExporting || !["Annual", "Monthly", "LTMM"].includes(timeStep)) return;

    if (!navigator.mediaDevices?.getDisplayMedia || typeof MediaRecorder === "undefined") {
      setError("This browser does not support recording the dashboard tab. Use the latest Chrome or Edge and allow tab sharing.");
      return;
    }

    const years = (selectionType === "basin" ? basinYears : YEARS)
      .map((value) => String(value))
      .filter((value) => /^\d{4}$/.test(value))
      .sort((a, b) => Number(a) - Number(b));
    const originalYear = String(year);
    const originalMonth = String(month);
    const isAnnualRecording = timeStep === "Annual";
    const frames = isAnnualRecording
      ? years.map((value) => ({ year: value, month: originalMonth, label: value }))
      : MONTHS.map((item) => ({
          year: originalYear,
          month: String(item.value),
          label: timeStep === "LTMM" ? item.label : `${item.label} ${originalYear}`,
        }));

    if (!frames.length) {
      setError(isAnnualRecording ? "No annual years are available to record." : "No monthly frames are available to record.");
      return;
    }

    stopAnimation();
    setGifExporting(true);
    setGifExportProgress("Select the dashboard browser tab (including the IWMI header and dashboard panels)…");
    setError("");
    let stream;
    let recorder;

    try {
      // Record the browser tab containing the dashboard, not the local desktop.
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 12, max: 20 },
          displaySurface: "browser",
          preferCurrentTab: true,
          selfBrowserSurface: "include",
          surfaceSwitching: "exclude",
          monitorTypeSurfaces: "exclude",
        },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: "include",
        surfaceSwitching: "exclude",
        monitorTypeSurfaces: "exclude",
      });
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) throw new Error("No dashboard-tab video track was provided.");

      const mimeCandidates = [
        "video/mp4;codecs=avc1.42E01E",
        "video/mp4",
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
      ];
      const mimeType = mimeCandidates.find((type) => MediaRecorder.isTypeSupported?.(type)) || "";
      const extension = mimeType.includes("mp4") ? "mp4" : "webm";
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunks.push(event.data);
      };
      const recordingDone = new Promise((resolve, reject) => {
        recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType || mimeType || "video/webm" }));
        recorder.onerror = (event) => reject(event.error || new Error("Dashboard recording failed."));
      });
      videoTrack.addEventListener("ended", () => {
        if (recorder && recorder.state !== "inactive") recorder.stop();
      }, { once: true });
      recorder.start(250);

      for (let index = 0; index < frames.length; index += 1) {
        if (videoTrack.readyState === "ended") break;
        const frame = frames[index];
        setGifExportProgress(`Recording dashboard · ${frame.label} (${index + 1}/${frames.length})…`);
        if (isAnnualRecording) setYear(frame.year);
        setMonth(frame.month);
        const result = await fetchSankey(buildPayload({
          selectedYear: frame.year,
          selectedMonth: frame.month,
          selectedTimeStep: timeStep,
        }));
        setHtmlUrl(result.htmlUrl);
        setCountrySankeyHtml(result.htmlDoc || "");
        setBasinPlotData(result.basinPlotData);
        setMeta(result.meta);
        setFlowStats(result.flowStats);
        // Let React repaint the dashboard and the selected period before capturing the next frame.
        await waitForAnimationFrame(1800);
      }

      if (recorder.state !== "inactive") recorder.stop();
      const blob = await recordingDone;
      if (!blob.size) throw new Error("The recording is empty. Keep the dashboard tab open and try again.");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = String(selectedName || "sankey").replace(/[^a-z0-9_-]+/gi, "_");
      const modeName = timeStep.toLowerCase();
      link.download = `${safeName}_${modeName}_IWMI_dashboard.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      setGifExportProgress(extension === "mp4"
        );
    } catch (exportError) {
      if (recorder && recorder.state !== "inactive") recorder.stop();
      setError(exportError?.message || "Unable to record the IWMI dashboard tab. Select the browser tab showing the dashboard, not the entire screen.");
      setGifExportProgress("");
    } finally {
      if (stream) stream.getTracks().forEach((track) => track.stop());
      setYear(originalYear);
      setMonth(originalMonth);
      try {
        const result = await fetchSankey(buildPayload({
          selectedYear: originalYear,
          selectedMonth: originalMonth,
          selectedTimeStep: timeStep,
        }));
        setHtmlUrl(result.htmlUrl);
        setCountrySankeyHtml(result.htmlDoc || "");
        setBasinPlotData(result.basinPlotData);
        setMeta(result.meta);
        setFlowStats(result.flowStats);
      } catch (restoreError) {
        // Keep the last successfully rendered frame if restoring fails.
      }
      setGifExporting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const runAnimation = async () => {
      if (!isAnimating) return;

      let frameCount = 0;
      try {
        setAnimationLoading(true);

        const availableYears = (
          selectionType === "basin" && basinYears.length
            ? basinYears
            : YEARS
        )
          .map(String)
          .filter((value) => /^\d{4}$/.test(value))
          .sort((a, b) => Number(a) - Number(b));

        let framePayload;
        let frameLabel;

        if (timeStep === "LTMM" || timeStep === "Monthly") {
          // Both modes advance month-by-month. LTMM uses climatological monthly
          // values; Monthly uses the selected year and that month's data.
          const monthIndex = animationStep % MONTHS.length;
          const activeMonth = MONTHS[monthIndex];
          const activeYear = String(year);
          framePayload = buildPayload({
            selectedYear: activeYear,
            selectedMonth: activeMonth.value,
          });
          frameLabel = timeStep === "LTMM"
            ? `Long-term mean monthly · ${activeMonth.label}`
            : `Monthly · ${activeMonth.label} ${activeYear}`;
          frameCount = MONTHS.length;
          setAnimationFrameMonth(String(activeMonth.value));
          setAnimationFrameYear(timeStep === "Monthly" ? activeYear : "");
          setAnimationLabel(frameLabel);
        } else {
          if (!availableYears.length) {
            throw new Error("No years are available for the selected country or basin.");
          }

          const activeYear = availableYears[animationStep % availableYears.length];
          framePayload = buildPayload({ selectedYear: activeYear });
          frameLabel = `Annual · ${activeYear}`;
          frameCount = availableYears.length;
          setAnimationFrameYear(String(activeYear));
          setAnimationFrameMonth("");
          setAnimationLabel(frameLabel);
          setYear(String(activeYear));
        }

        const result = await fetchSankey(framePayload);
        if (cancelled) return;

        setHtmlUrl(result.htmlUrl);
        // Country Sankey is inside an HTML iframe; update its srcDoc on every frame.
        setCountrySankeyHtml(result.htmlDoc || "");
        // Basin Sankey is rendered from the returned plot data.
        setBasinPlotData(result.basinPlotData);
        setMeta(result.meta);
        setFlowStats(result.flowStats);
        setAnimationLabel(frameLabel);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Animation failed");
          setIsAnimating(false);
        }
        return;
      } finally {
        if (!cancelled) setAnimationLoading(false);
      }

      if (!cancelled && isAnimating && frameCount > 0) {
        animationTimerRef.current = window.setTimeout(() => {
          setAnimationStep((previous) => (previous + 1) % frameCount);
        }, 2600);
      }
    };

    runAnimation();

    return () => {
      cancelled = true;
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, [
    isAnimating,
    animationStep,
    selectionType,
    country,
    basin,
    timeStep,
    month,
    height,
    basinYears,
  ]);

  const handleOpenCompare = () => {
    if (timeStep === "LTMA") return;
    stopAiAgent();
    setCompareError("");
    setCompareLeftHtml("");
    setCompareRightHtml("");
    setCompareLeftUrl("");
    setCompareRightUrl("");
    setCompareLeftHtml("");
    setCompareRightHtml("");
    setCompareLeftBasinPlotData(null);
    setCompareRightBasinPlotData(null);
    setCompareOpen(true);

    if (selectionType === "basin") {
      setCompareBasin(basin || "");
      setCompareTimeStep(timeStep || "Annual");

      const availableYears = basinYears.length ? basinYears : YEARS;
      const currentYear = String(year);
      const currentIndex = availableYears.indexOf(currentYear);
      const fallbackA = availableYears[currentIndex >= 0 ? currentIndex : 0] || "";
      const fallbackB = availableYears[currentIndex > 0 ? currentIndex - 1 : 1] || fallbackA;

      setCompareYearA(fallbackA);
      setCompareYearB(fallbackB);
      setCompareMonth(String(month));
      setCompareMonthA(String(month));
      setCompareMonthB(String(month));
    } else {
      setCompareCountry(country || "");
      setCompareTimeStep(timeStep || "Annual");
      const availableYears = YEARS;
      const currentYear = String(year);
      const currentIndex = availableYears.indexOf(currentYear);
      const fallbackA = availableYears[currentIndex >= 0 ? currentIndex : 0] || "";
      const fallbackB = availableYears[currentIndex > 0 ? currentIndex - 1 : 1] || fallbackA;
      setCompareYearA(fallbackA);
      setCompareYearB(fallbackB);
      setCompareMonth(String(month));
      setCompareMonthA(String(month));
      setCompareMonthB(String(month));
    }
  };

  const handleCloseCompare = () => {
    setCompareOpen(false);
    setCompareError("");
    setCompareLoading(false);
  };

  const handleRunComparison = async () => {
    const compareIsBasin = selectionType === "basin";
    const compareTarget = compareIsBasin ? compareBasin : compareCountry;

    if (!compareTarget) {
      setCompareError(
        compareIsBasin
          ? "Please select a basin before running the comparison."
          : "Please select a country before running the comparison."
      );
      return;
    }

    setCompareLoading(true);
    setCompareError("");
    setCompareLeftUrl("");
    setCompareRightUrl("");
    setCompareLeftBasinPlotData(null);
    setCompareRightBasinPlotData(null);
    setCompareLeftFlowStats(null);
    setCompareRightFlowStats(null);

    try {
      const leftPayload = buildPayload({
        selectedSelectionType: compareIsBasin ? "basin" : "country",
        selectedCountry: compareIsBasin ? "" : compareCountry,
        selectedBasin: compareIsBasin ? compareBasin : basin,
        selectedTimeStep: compareTimeStep,
        selectedYear: compareYearA,
        selectedMonth: showCompareMonth ? compareMonthA : compareMonth,
        selectedHeight: compareIsBasin ? 600 : 520,
      });

      const rightPayload = buildPayload({
        selectedSelectionType: compareIsBasin ? "basin" : "country",
        selectedCountry: compareIsBasin ? "" : compareCountry,
        selectedBasin: compareIsBasin ? compareBasin : basin,
        selectedTimeStep: compareTimeStep,
        selectedYear: compareYearB,
        selectedMonth: showCompareMonth ? compareMonthB : compareMonth,
        selectedHeight: compareIsBasin ? 600 : 520,
      });

      const [leftResult, rightResult] = await Promise.all([
        fetchSankey(leftPayload),
        fetchSankey(rightPayload),
      ]);

      setCompareLeftFlowStats(leftResult.flowStats || (leftResult.basinPlotData ? buildBasinFlowStats(leftResult.basinPlotData) : null));
      setCompareRightFlowStats(rightResult.flowStats || (rightResult.basinPlotData ? buildBasinFlowStats(rightResult.basinPlotData) : null));

      if (compareIsBasin) {
        setCompareLeftBasinPlotData(leftResult.basinPlotData || null);
        setCompareRightBasinPlotData(rightResult.basinPlotData || null);
      } else {
        // Country comparison remains exactly on the existing HTML/iframe path.
        setCompareLeftUrl(leftResult.htmlUrl || "");
        setCompareRightUrl(rightResult.htmlUrl || "");
        setCompareLeftHtml(leftResult.htmlDoc || "");
        setCompareRightHtml(rightResult.htmlDoc || "");
      }
    } catch (err) {
      setCompareError(err.message || "Comparison failed");
    } finally {
      setCompareLoading(false);
    }
  };

  const swapComparisonYears = () => {
    setCompareYearA(compareYearB);
    setCompareYearB(compareYearA);

    if (showCompareMonth) {
      setCompareMonthA(compareMonthB);
      setCompareMonthB(compareMonthA);
    }

    if (selectionType === "basin") {
      setCompareLeftBasinPlotData(compareRightBasinPlotData);
      setCompareRightBasinPlotData(compareLeftBasinPlotData);
      setCompareLeftFlowStats(compareRightFlowStats);
      setCompareRightFlowStats(compareLeftFlowStats);
    } else {
      setCompareLeftUrl(compareRightUrl);
      setCompareRightUrl(compareLeftUrl);
      setCompareLeftHtml(compareRightHtml);
      setCompareRightHtml(compareLeftHtml);
    }
  };

  const getBasinCompareCaption = (yearValue, monthValue) => {
    const displayEntity = String(compareBasin || "Basin")
      .replaceAll("_", " ")
      .trim();

    if (compareTimeStep === "Annual") {
      return `${displayEntity} • Annual • ${yearValue}`;
    }

    if (compareTimeStep === "Monthly") {
      return `${displayEntity} • Monthly • ${yearValue} • ${getMonthLabel(monthValue)}`;
    }

    if (compareTimeStep === "LTMM") {
      return `${displayEntity} • Long-term mean monthly • ${getMonthLabel(monthValue)}`;
    }

    return `${displayEntity} • ${getTimeStepLabel(compareTimeStep)}`;
  };

  const getCompareCaption = (yearValue, monthValue = compareMonth) => {
    const entity = selectionType === "basin" ? compareBasin : compareCountry;
    const displayEntity = String(entity || "")
      .replaceAll("_", " ")
      .trim();

    if (compareTimeStep === "LTMA") {
      return `${displayEntity} • ${getTimeStepLabel(compareTimeStep)}`;
    }

    if (compareTimeStep === "LTMM") {
      return `${displayEntity} • ${getTimeStepLabel(compareTimeStep)} • ${getMonthLabel(monthValue)}`;
    }

    if (compareTimeStep === "Annual") {
      return `${displayEntity} • ${getTimeStepLabel(compareTimeStep)} • ${yearValue}`;
    }

    if (compareTimeStep === "Monthly") {
      return `${displayEntity} • ${getTimeStepLabel(compareTimeStep)} • ${yearValue} • ${getMonthLabel(monthValue)}`;
    }

    return `${displayEntity} • ${compareTimeStep}`;
  };

  const renderCompareBasinPlot = (plotData, plotKey) => {
    if (!plotData) return null;

    const labels = Array.isArray(plotData?.nodes?.label) ? plotData.nodes.label : [];
    const sources = Array.isArray(plotData?.links?.source) ? plotData.links.source : [];
    const targets = Array.isArray(plotData?.links?.target) ? plotData.links.target : [];
    const values = Array.isArray(plotData?.links?.value) ? plotData.links.value : [];
    const coordinates = getBasinDisplayCoordinates(plotData?.coordinates, labels, plotData?.countries || []);
    const zoomedCoordinates = coordinates;
    const hasCoordinates = Boolean(zoomedCoordinates && coordinates?.source !== "fallback");
    const nodeColors = getBasinNodeColors(labels, plotData?.nodes?.color || []);
    const linkColors = sources.map((source, index) => {
      const target = targets[index];
      const sourceColor = nodeColors[Number(source)] || BASIN_VIBRANT_COLORS[0];
      const targetColor = nodeColors[Number(target)] || sourceColor;
      // Keep link colors consistent with the country Sankey palette while
      // retaining a distinct, readable color for each source pathway.
      return hexToRgba(sourceColor === targetColor ? sourceColor : sourceColor, 0.56);
    });

    return (
      <Plot
        key={plotKey}
        data={[{
          type: "sankey",
          orientation: "h",
          arrangement: hasCoordinates ? "freeform" : "snap",
          domain: { x: [0, 1], y: [0.10, 0.87] },
          textfont: { family: "Inter, Arial, sans-serif", size: 10, color: "#173f7a" },
          node: {
            label: labels,
            color: nodeColors,
            customdata: plotData?.nodes?.hover || [],
            ...(hasCoordinates ? { x: zoomedCoordinates.x, y: zoomedCoordinates.y } : {}),
            pad: 16,
            thickness: 8,
            line: { color: "rgba(16,32,51,0.28)", width: 1.1 },
            hovertemplate: "%{customdata}<extra></extra>",
          },
          link: {
            source: sources,
            target: targets,
            value: values,
            color: linkColors,
            label: plotData?.links?.label || [],
            customdata: plotData?.links?.percent_out || [],
            hovertemplate:
              "%{label}<br>Flow: %{value:.2f} " +
              (plotData?.meta?.flow_unit || "km³") +
              "/" +
              (plotData?.meta?.time_unit || "year") +
              "<br>%{customdata:.1f}% of total outflow<extra></extra>",
          },
        }]}
        layout={{
          autosize: true,
          margin: { l: 6, r: 6, t: 12, b: 4 },
          font: { family: "Inter, Arial, sans-serif", size: 10, color: palette.text },
          paper_bgcolor: "#ffffff",
          plot_bgcolor: "#ffffff",
          hoverlabel: { bgcolor: "#102033", bordercolor: "#102033", font: { color: "#ffffff", size: 12 } },
        }}
        config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ["lasso2d", "select2d"] }}
        useResizeHandler
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    );
  };

  const isRiverAnimationView =
    selectionType === "basin" &&
    basinViewTab === "animation" &&
    Boolean(basinAnimationUrl);

  const showSankeyAiPanel =
    Boolean(htmlUrl || basinPlotData) &&
    !(selectionType === "basin" && basinViewTab === "animation");

  const animationPeriodLabel =
    basinAnimationData?.periodLabel ||
    riverAnimationCurrentPeriod?.label ||
    `${getMonthLabel(month)} ${year}`;

  const animationDisplayType =
    riverAnimationMode === "fixed-month-years"
      ? `${getMonthLabel(month)} comparison across years`
      : riverAnimationMode === "annual-years"
      ? "Annual progression across years"
      : `Monthly progression within ${year}`;

  const animationModeLabel = riverAnimationModeInfo?.label || "Monthly progression within selected year";

  const animationActionLabel = riverAnimationPlaying
    ? "Pause river animation"
    : "Play river animation";

  const animationRenderVersion =
    basinAnimationData?.render_version ||
    basinAnimationData?.version ||
    "Current renderer";

  const flowUnit = meta?.flowUnit || basinPlotData?.meta?.flow_unit || "flow units";
  const timeUnit = meta?.timeUnit || basinPlotData?.meta?.time_unit || "period";
  const fullUnit = `${flowUnit}/${timeUnit}`;

  const diagramLinks = useMemo(() => {
    if (Array.isArray(flowStats?.links) && flowStats.links.length) {
      return flowStats.links
        .filter((item) => toNumber(item?.value) !== null)
        .map((item) => ({
          ...item,
          value: toNumber(item.value),
        }))
        .sort((a, b) => b.value - a.value);
    }

    if (basinPlotData?.links?.value?.length && basinPlotData?.nodes?.label) {
      return buildBasinFlowStats(basinPlotData)?.links || [];
    }

    return [];
  }, [flowStats, basinPlotData]);

  const visibleDiagramTotal = useMemo(
    () => diagramLinks.reduce((sum, item) => sum + (toNumber(item.value) || 0), 0),
    [diagramLinks]
  );

  const basinFinalOutflowInfo = useMemo(
    () => getBasinFinalOutflowInfo(basinPlotData, diagramLinks),
    [basinPlotData, diagramLinks]
  );

  const inflowDisplay = flowStats?.hasDynamicValues
    ? formatFlow(flowStats.inflow, fullUnit) ||
      formatFlow(visibleDiagramTotal, fullUnit) ||
      "Available in diagram"
    : formatFlow(visibleDiagramTotal, fullUnit) || "Diagram loaded";

  const outflowDisplay =
    selectionType === "basin"
      ? basinFinalOutflowInfo?.links?.length
        ? basinFinalOutflowInfo.links
            .map((link) => `${link.source || "Source"} → ${link.target || "Target"}: ${formatFlow(link.value, fullUnit)}`)
            .join("; ")
        : diagramLinks.length
        ? diagramLinks.slice(0, 3)
            .map((link) => `${link.source || "Source"} → ${link.target || "Target"}: ${formatFlow(link.value, fullUnit)}`)
            .join("; ")
        : "No numeric pathway values"
      : flowStats?.outflow !== null && flowStats?.outflow !== undefined
      ? formatFlow(flowStats.outflow, fullUnit) || "Available in diagram"
      : "See diagram pathways";

  const dominantLink = diagramLinks[0] || null;

  const topPathwayDisplay = dominantLink
    ? `${dominantLink.source || "Source"} → ${dominantLink.target || "Target"}`
    : flowStats?.inflowBreakdown?.[0]?.name || "Diagram loaded";

  const aiSummary = useMemo(() => {
    const name = meta?.country || meta?.basin || selectedName || "the selected area";

    if (isRiverAnimationView) {
      const riverData = basinAnimationData?.riverData;
      const periodLabel =
        basinAnimationData?.periodLabel ||
        riverAnimationCurrentPeriod?.label ||
        `${getMonthLabel(month)} ${year}`;

      const segmentValues = Array.isArray(riverData?.segments)
        ? riverData.segments
            .map((segment, index) => {
              const value = toNumber(
                Array.isArray(segment?.monthlyValues)
                  ? segment.monthlyValues[0]
                  : null
              );

              return value === null
                ? null
                : {
                    name:
                      segment?.name ||
                      `Segment ${index + 1}`,
                    value,
                  };
            })
            .filter(Boolean)
        : [];

      if (!segmentValues.length) {
        return `The ${basin} river-flow animation is showing ${periodLabel}. River-flow values are expressed in km³. Wider blue river sections indicate stronger reference flow, while the displayed numeric labels show the period-specific flow on individual river segments.`;
      }

      const positiveSegments =
        segmentValues.filter(
          (item) => item.value > 0
        );

      const mean =
        positiveSegments.length
          ? positiveSegments.reduce(
              (sum, item) =>
                sum + item.value,
              0
            ) / positiveSegments.length
          : 0;

      const sorted =
        [...positiveSegments].sort(
          (a, b) => b.value - a.value
        );

      const strongest = sorted[0];
      const weakest =
        sorted[sorted.length - 1];

      const lowFlowCount =
        segmentValues.filter(
          (item) => item.value <= 0.01
        ).length;

      const aboveMeanCount =
        positiveSegments.filter(
          (item) => item.value > mean
        ).length;

      return `For the ${basin} basin during ${periodLabel}, ${
        positiveSegments.length
      } of ${segmentValues.length} mapped river segments have positive flow values. The mean positive segment flow is ${mean.toFixed(
        2
      )} km³, with ${aboveMeanCount} segments above this network mean. ${
        strongest
          ? `The highest displayed segment flow is ${strongest.value.toFixed(
              2
            )} km³ on ${strongest.name}.`
          : ""
      } ${
        weakest
          ? `The lowest positive segment flow is ${weakest.value.toFixed(
              2
            )} km³ on ${weakest.name}.`
          : ""
      } ${
        lowFlowCount
          ? `${lowFlowCount} segments are at or below 0.01 km³, indicating very low or negligible flow for the displayed period.`
          : ""
      } The map labels use km³ and should be interpreted together with the river network pattern and relative ribbon widths.`
        .replace(/\s+/g, " ")
        .trim();
    }

    const period =
      meta?.timePeriod ||
      (showYear
        ? timeStep === "Monthly"
          ? `${getMonthLabel(month)} ${year}`
          : year
        : getTimeStepLabel(timeStep));

    if (!htmlUrl && !basinPlotData) {
      return "Generate a Sankey diagram to view its flow summary.";
    }

    // Basin summary: describe the displayed flow pathways in technical hydrological language.
    // Every link is reported individually; internal transfers are never summed into a basin total.
    if (selectionType === "basin") {
      const outletInfo = basinFinalOutflowInfo || { links: [], value: null, source: null };
      const outletLinks = (outletInfo.links || []).filter((link) => toNumber(link?.value) !== null);
      const pathwayText = (link) =>
        `${link.source || "Source"} → ${link.target || "Target"}: ${formatFlow(link.value, fullUnit)}`;
      const outletKeys = new Set(outletLinks.map((link) => `${link.source || ""} → ${link.target || ""}`));
      const otherPathways = diagramLinks
        .filter((link) => !outletKeys.has(`${link.source || ""} → ${link.target || ""}`))
        .filter((link) => toNumber(link?.value) !== null)
        .slice(0, 4);
      const pathwayContext = otherPathways.length
        ? ` Other prominent individual pathways are ${otherPathways.map(pathwayText).join("; ")}.`
        : "";

      if (outletLinks.length) {
        const outletDescription = outletLinks.length === 1
          ? `The terminal pathway is ${pathwayText(outletLinks[0])}. Its displayed discharge is reported as the basin outflow.`
          : `The diagram shows these terminal pathways: ${outletLinks.map(pathwayText).join("; ")}. Their values are reported separately.`;
        return `Hydrological interpretation — ${name} basin (${period}): ${outletDescription}${pathwayContext} These values represent flow along individual links in the displayed river network. Intermediate country-to-country transfers are not added to terminal discharge. Flow values are expressed in ${fullUnit}.`;
      }

      const prominent = diagramLinks
        .filter((link) => toNumber(link?.value) !== null)
        .slice(0, 5);
      if (prominent.length) {
        const leading = prominent.slice(0, 2).map(pathwayText).join("; ");
        const additional = prominent.slice(2).map(pathwayText);
        const additionalText = additional.length ? ` Other notable pathways are ${additional.join("; ")}.` : "";
        return `Hydrological interpretation — ${name} basin (${period}): The Sankey indicates the strongest individual water-transfer pathways as ${leading}.${additionalText} The pathway values describe transfers between the labelled nodes and are interpreted independently; they are not summed to create a basin-wide outflow. The final basin outflow should correspond to discharge crossing the basin outlet, while internal transfers describe redistribution within the network. Flow values are expressed in ${fullUnit}.`;
      }

      return `Hydrological interpretation — ${name} basin (${period}): The Sankey is displayed, but its current response contains no numeric link values for a quantitative pathway summary. Flow interpretation requires the diagram's source nodes, target nodes, and corresponding numeric discharge values. Values should be reported per pathway, with internal transfers kept separate from terminal basin outflow.`;
    }

    // Use the links that actually draw the diagram whenever they are available.
    if (diagramLinks.length) {
      const total = visibleDiagramTotal;
      const topLinks = diagramLinks.slice(0, 5);
      const top = topLinks[0];

      const nodeNames = [
        ...new Set(
          diagramLinks
            .flatMap((item) => [item.source, item.target])
            .filter(Boolean)
        ),
      ];

      const topShare = getPercent(top.value, total);

      const topThreeValue = topLinks
        .slice(0, 3)
        .reduce((sum, item) => sum + (toNumber(item.value) || 0), 0);

      const topFiveValue = topLinks.reduce(
        (sum, item) => sum + (toNumber(item.value) || 0),
        0
      );

      const topThreeShare = getPercent(topThreeValue, total);
      const topFiveShare = getPercent(topFiveValue, total);

      const strongestText = `${top.source || "Source"} → ${
        top.target || "Target"
      } carries ${
        formatFlow(top.value, fullUnit) || "the largest displayed flow"
      }${
        topShare !== null
          ? `, equivalent to ${topShare.toFixed(
              1
            )}% of the total visible Sankey link flow`
          : ""
      }.`;

      const pathwayText = topLinks
        .slice(1)
        .map((item, index) => {
          const share = getPercent(item.value, total);

          return `${index + 2}) ${item.source || "Source"} → ${
            item.target || "Target"
          } carries ${
            formatFlow(item.value, fullUnit) || "the displayed value"
          }${
            share !== null ? ` (${share.toFixed(1)}% of visible flow)` : ""
          }`;
        })
        .join("; ");

      const concentrationText =
        topThreeShare !== null
          ? topThreeShare >= 75
            ? `The network is strongly concentrated, with the three largest pathways contributing ${topThreeShare.toFixed(
                1
              )}% of all visible link flow. This means most of the water represented in the diagram moves through only a small number of dominant connections.`
            : topThreeShare >= 50
            ? `The network shows moderate concentration: the three largest pathways contribute ${topThreeShare.toFixed(
                1
              )}% of visible flow. A few connections dominate, but a meaningful share is still distributed across the remaining pathways.`
            : `The network is comparatively distributed: the three largest pathways contribute only ${topThreeShare.toFixed(
                1
              )}% of visible flow, indicating that water movement is spread across several connections rather than being controlled by only a few links.`
          : "";

      const topFiveText =
        topFiveShare !== null && diagramLinks.length > 3
          ? `Together, the five largest pathways represent ${topFiveShare.toFixed(
              1
            )}% of the total visible link flow.`
          : "";

      const focusIncoming =
        flowStats?.inflow !== null && flowStats?.inflow !== undefined
          ? toNumber(flowStats.inflow)
          : null;

      const focusOutgoing =
        flowStats?.outflow !== null && flowStats?.outflow !== undefined
          ? toNumber(flowStats.outflow)
          : null;

      let focusText = "";

      if (flowStats?.focusLabel) {
        const incomingLabel =
          focusIncoming !== null
            ? formatFlow(focusIncoming, fullUnit)
            : "the value represented by its incoming links";

        const outgoingLabel =
          focusOutgoing !== null
            ? formatFlow(focusOutgoing, fullUnit)
            : "the value represented by its outgoing links";

        let balanceText = "";

        if (
          focusIncoming !== null &&
          focusOutgoing !== null &&
          focusIncoming !== 0
        ) {
          const difference = focusIncoming - focusOutgoing;
          const differencePercent =
            (Math.abs(difference) / Math.abs(focusIncoming)) * 100;

          if (Math.abs(difference) < Math.abs(focusIncoming) * 0.01) {
            balanceText =
              " Incoming and outgoing flows are nearly balanced in the displayed network.";
          } else if (difference > 0) {
            balanceText = ` Incoming flow exceeds outgoing flow by ${
              formatFlow(Math.abs(difference), fullUnit) ||
              Math.abs(difference).toLocaleString()
            }, approximately ${differencePercent.toFixed(
              1
            )}% of incoming flow.`;
          } else {
            balanceText = ` Outgoing flow exceeds incoming flow by ${
              formatFlow(Math.abs(difference), fullUnit) ||
              Math.abs(difference).toLocaleString()
            }, approximately ${differencePercent.toFixed(
              1
            )}% of incoming flow.`;
          }
        }

        focusText = `For ${flowStats.focusLabel}, the diagram-derived incoming flow is ${incomingLabel} and the outgoing flow is ${outgoingLabel}.${balanceText}`;
      }

      const structureText = `The diagram contains ${diagramLinks.length} visible pathways connecting ${nodeNames.length} distinct nodes. The combined value of all displayed Sankey links is ${
        formatFlow(total, fullUnit) || "the total represented by the visible links"
      }.`;

      const interpretationText =
        topShare !== null && topShare >= 40
          ? `The widest Sankey band is therefore a particularly important connection in the selected period and should be treated as the primary flow route in the diagram.`
          : `No single pathway completely dominates the network, so the overall pattern should be interpreted using several of the larger bands together rather than only the widest connection.`;

      return `For ${name} during ${period}, ${structureText} The dominant pathway is ${strongestText} ${
        pathwayText
          ? `The other major pathways are ${pathwayText}.`
          : ""
      } ${concentrationText} ${topFiveText} ${focusText} ${interpretationText} Sankey band width is proportional to the displayed flow value, so wider bands represent larger transfers and narrower bands represent smaller contributions.`
        .replace(/\s+/g, " ")
        .trim();
    }

    // Final fallback: the diagram is visible even if its HTML did not expose
    // machine-readable link arrays. Never leave the summary area blank.
    if (flowStats?.hasDynamicValues) {
      const inflowText = buildFlowListText(
        flowStats.inflowBreakdown,
        fullUnit,
        "inflow"
      );
      const outflowText = buildFlowListText(
        flowStats.outflowBreakdown,
        fullUnit,
        "outflow"
      );

      return `For ${name} during ${period}, the Sankey diagram is loaded. ${
        flowStats.inflow !== null && flowStats.inflow !== undefined
          ? `Reported inflow is ${formatFlow(flowStats.inflow, fullUnit)}.`
          : ""
      } ${
        flowStats.outflow !== null && flowStats.outflow !== undefined
          ? `Reported outflow is ${formatFlow(flowStats.outflow, fullUnit)}.`
          : ""
      } ${inflowText ? `Major inflows: ${inflowText}.` : ""} ${
        outflowText ? `Major outflows: ${outflowText}.` : ""
      }`.replace(/\s+/g, " ").trim();
    }

    return `The Sankey diagram for ${name} during ${period} has loaded successfully, but its link values are not exposed as readable JSON in the current backend response or generated HTML. The diagram can still be viewed, but a value-based AI summary requires the Sankey node/link arrays to be returned by the backend or embedded in the Plotly HTML.`;
  }, [
    basin,
    basinPlotData,
    basinFinalOutflowInfo,
    selectionType,
    diagramLinks,
    flowStats,
    fullUnit,
    htmlUrl,
    isRiverAnimationView,
    meta,
    month,
    selectedName,
    showYear,
    timeStep,
    visibleDiagramTotal,
    year,
    basinAnimationData?.riverData,
    basinAnimationData?.periodLabel,
    riverAnimationCurrentPeriod?.label,
  ]);

  const startAiAgent = () => {
    if (!aiSummary) return;

    stopAiAgent();

    if (typeof window === "undefined" || !window.speechSynthesis) {
      setError("Voice narration is not supported in this browser.");
      return;
    }

    setIsAiAgentRunning(true);

    const utterance = new SpeechSynthesisUtterance(aiSummary);
    utterance.rate = 0.88;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setIsSpeaking(true);

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsAiAgentRunning(false);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsAiAgentRunning(false);
    };

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  const responsiveLayout = isSmallScreen
    ? { gridTemplateColumns: "1fr" }
    : isMediumScreen
    ? { gridTemplateColumns: "300px minmax(0, 1fr)" }
    : {};

  const responsiveHeader = isSmallScreen
    ? { gridTemplateColumns: "1fr", textAlign: "center" }
    : {};

  const responsiveHeaderActions = isSmallScreen
    ? {
        minWidth: 0,
        justifyContent: "center",
        flexWrap: "wrap",
      }
    : {};

  const responsiveMetaGrid = isSmallScreen
    ? { gridTemplateColumns: "1fr 1fr" }
    : isMediumScreen
    ? { gridTemplateColumns: "repeat(2, minmax(150px, 1fr))" }
    : {};

  const responsiveAiGrid = isSmallScreen
    ? { gridTemplateColumns: "1fr" }
    : isMediumScreen
    ? { gridTemplateColumns: "1fr" }
    : {};

  const responsiveAnimationWorkspace =
    isSmallScreen || isMediumScreen
      ? { gridTemplateColumns: "1fr" }
      : {};

  const responsiveAnimationStage = isSmallScreen
    ? { height: 520, minHeight: 520 }
    : isMediumScreen
    ? { height: 620, minHeight: 620 }
    : {};

  const basinNodeCount = basinPlotData?.nodes?.label?.length || 0;
  const basinDisplayCoordinates = getBasinDisplayCoordinates(
    basinPlotData?.coordinates,
    basinPlotData?.nodes?.label || basinNodeCount,
    basinPlotData?.countries || []
  );
  const basinHasDisplayCoordinates = Boolean(basinDisplayCoordinates);
  const basinTitle =
    basinPlotData?.meta?.title ||
    String(basin || "Basin").replaceAll("_", " ");
  const isVoltaBasin = String(basin || basinPlotData?.meta?.title || "")
    .trim()
    .toLowerCase()
    .includes("volta");
  const basinUnit = basinPlotData?.meta?.flow_unit || "km³";
  const basinTimeUnit = basinPlotData?.meta?.time_unit || "year";
  const basinPeriod = timeStep || "Selected period";
  const basinZoomedCoordinates = basinDisplayCoordinates;
  const basinNodeLabels = basinPlotData?.nodes?.label || [];
  const basinNodeColors = getBasinNodeColors(
    basinNodeLabels,
    basinPlotData?.nodes?.color || []
  );
  const basinLinkColors = (basinPlotData?.links?.source || []).map((source) =>
    hexToRgba(basinNodeColors[Number(source)] || "#4d7fb8", 0.64)
  );

  const selectedEntityDisplay = String(selectionType === "basin" ? basin : country || "")
    .replaceAll("_", " ")
    .trim() || (selectionType === "basin" ? "Basin" : "Country");

  const selectedPeriodDisplay =
    timeStep === "LTMA"
      ? "Long-term mean annual"
      : timeStep === "LTMM"
      ? `Long-term mean monthly · ${getMonthLabel(month)}`
      : timeStep === "Annual"
      ? `Annual · ${year}`
      : `Monthly · ${getMonthLabel(month)} ${year}`;

  const mainVisualTitle =
    selectionType === "basin"
      ? `${selectedEntityDisplay} Basin Water Flow`
      : `${selectedEntityDisplay} Country Water Flow`;

  const mainVisualSubtitle = `${isAnimating && animationLabel ? animationLabel : selectedPeriodDisplay} · WaterTracker Sankey`;


  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <header
          style={{
            ...styles.dashboardHeader,
            ...responsiveHeader,
            ...(headerCompact
              ? {
                  top: 0,
                  minHeight: 54,
                  height: 54,
                  boxSizing: "border-box",
                  gridTemplateColumns: isSmallScreen ? "auto minmax(0, 1fr) auto" : "auto minmax(0, 1fr) auto",
                  padding: isSmallScreen ? "4px 8px" : "4px 12px",
                  marginBottom: 6,
                  borderRadius: "0 0 12px 12px",
                  boxShadow: "0 5px 18px rgba(8,26,44,0.16)",
                  background: "rgba(18,50,74,0.97)",
                  gap: 8,
                  transition: "padding 160ms ease, height 160ms ease, min-height 160ms ease, border-radius 160ms ease",
                }
              : {}),
          }}
        >
          <div style={styles.headerBrand}>
            <div style={styles.logoGroup}>
              <img src={IWMI_Logo} alt="IWMI" style={{ ...styles.logo, ...(headerCompact ? { maxHeight: 22, maxWidth: 72 } : {}) }} />
              <span style={styles.logoDivider} />
              <img src={CGIAR_transparent} alt="CGIAR" style={{ ...styles.logo, ...(headerCompact ? { maxHeight: 22, maxWidth: 72 } : {}) }} />
            </div>
          </div>

          <div style={styles.titleBlock}>
            <h1 style={{ ...styles.appTitle, ...(headerCompact ? { fontSize: isSmallScreen ? 13 : 16, lineHeight: 1, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}) }}>Transboundary Water Tracker</h1>
            <p style={{ ...styles.appSubtitle, ...(headerCompact ? { display: "none" } : {}) }}>
              SIWA+ dashboard for basin-scale water accounting, comparison, and
              flow visualization.
            </p>
          </div>

          <div style={{ ...styles.headerActions, ...responsiveHeaderActions }}>
            <button style={{ ...styles.homeButton, ...(headerCompact ? { padding: "5px 9px", fontSize: 11, whiteSpace: "nowrap" } : {}) }} onClick={goHome}>
              ← Home
            </button>
          </div>
        </header>

        <div style={{ ...styles.layout, ...responsiveLayout }}>
          <aside
            style={{
              ...styles.card,
              ...styles.sidebar,
              ...(isSmallScreen ? { position: "relative", top: 0 } : {}),
            }}
          >
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Configuration</h2>
              <p style={styles.sectionText}>
                Select Country or Basin first, then choose the time settings and
                generate the diagram.
              </p>
            </div>

            <div style={styles.radioPanel}>
              {[
                { value: "country", label: "Country" },
                { value: "basin", label: "Basin" },
              ].map((item) => (
                <label
                  key={item.value}
                  style={{
                    ...styles.radioOption,
                    borderColor:
                      selectionType === item.value
                        ? palette.teal
                        : palette.borderStrong,
                    background:
                      selectionType === item.value
                        ? palette.primarySoft
                        : "#ffffff",
                  }}
                >
                  <input
                    type="radio"
                    name="selectionType"
                    value={item.value}
                    checked={selectionType === item.value}
                    onChange={() => {
                      setSelectionType(item.value);
                       if (item.value === "basin") {
                         const nextBasinYear = basinYears.includes(String(year))
                           ? String(year)
                           : basinYears[0];

                         if (nextBasinYear) setYear(nextBasinYear);
                       }

                      stopAiAgent();
                      setHtmlUrl("");
                      setCountrySankeyHtml("");
                      setBasinPlotData(null);
                      setMeta(null);
                      setFlowStats(null);
                      setError("");
                    }}
                  />

                  {item?.label}
                </label>
              ))}
            </div>

            <div style={styles.fieldsPanel}>
              {selectionType === "country" ? (
                <div style={styles.group}>
                  <label style={styles.label}>Choose country</label>

                  <select
                    style={styles.input}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={countriesLoading || !countries.length}
                  >
                    {countriesLoading ? (
                      <option value="">Loading countries...</option>
                    ) : !countries.length ? (
                      <option value="">No countries available</option>
                    ) : (
                      countries.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))
                    )}
                  </select>

                  {countriesError ? (
                    <div style={styles.error}>{countriesError}</div>
                  ) : null}
                </div>
              ) : (
                <div style={styles.group}>
                  <label style={styles.label}>Choose basin</label>

                  <select
                     style={styles.input}
                     value={basin}
                     onChange={(e) => setBasin(e.target.value)}
                     disabled={basinsLoading || !basins.length}
                   >
                     {basinsLoading ? (
                       <option value="">Loading basins...</option>
                     ) : !basins.length ? (
                       <option value="">No basins available</option>
                     ) : (
                       basins.map((item) => (
                         <option key={item.id} value={item.id}>
                           {item.name}
                         </option>
                       ))
                     )}
                   </select>

                   {basinsError ? (
                     <div style={styles.error}>{basinsError}</div>
                   ) : null}
                </div>
              )}

              <div style={styles.group}>
                <label style={styles.label}>Time step</label>

                <select
                  style={styles.input}
                  value={timeStep}
                  onChange={(e) => setTimeStep(e.target.value)}
                >
                  {availableTimeSteps.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item?.label}
                    </option>
                  ))}
                </select>
              </div>

              {(showYear || showMonth) && (
                <div style={styles.gridTwo}>
                  {showYear && (
                    <div style={styles.group}>
                      <label style={styles.label}>Year</label>

                      <select
                        style={styles.input}
                        value={isAnimating && animationFrameYear ? animationFrameYear : year}
                        onChange={(e) => setYear(e.target.value)}
                      >
                        {(selectionType === "basin" ? basinYears : YEARS).map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {showMonth && (
                    <div style={styles.group}>
                      <label style={styles.label}>Month</label>

                      <select
                        style={styles.input}
                        value={isAnimating && animationFrameMonth ? animationFrameMonth : month}
                        onChange={(e) => setMonth(e.target.value)}
                      >
                        {MONTHS.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {selectionType === "country" ? (
                <div style={styles.group}>
                  <label style={styles.label}>Chart height</label>

                  <select
                    style={styles.input}
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                  >
                    {chartHeightOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item?.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            <button
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {}),
              }}
              onClick={handleGenerate}
              disabled={loading || (selectionType === "country" && !country)}
            >
              {loading
                ? "Generating diagram..."
                : selectionType === "basin"
                ? "Generate basin visualization"
                : "Generate Sankey diagram"}
            </button>

            <div
              style={
                showAnimationButton
                  ? styles.gridTwo
                  : { display: "grid", gridTemplateColumns: "1fr" }
              }
            >
              {showAnimationButton ? (
                <button
                  style={{
                    ...styles.secondaryButton,
                    ...(animationLoading ? styles.buttonDisabled : {}),
                  }}
                  onClick={handleStartAnimation}
                  disabled={animationLoading || gifExporting}
                >
                  {isAnimating ? "Animation running..." : (timeStep === "LTMM" || timeStep === "Monthly") ? "Start Animation" : "Start animation"}
                </button>
              ) : null}

              {["Annual", "Monthly", "LTMM"].includes(timeStep) ? (
                <>
                  <button
                    title="Records the dashboard browser tab, including the IWMI/CGIAR header, controls, and visible panels. Choose the dashboard tab in the browser prompt."
                    style={{
                      ...styles.secondaryButton,
                      ...(gifExporting ? styles.buttonDisabled : {}),
                    }}
                    onClick={handleDownloadAnnualVideo}
                    disabled={gifExporting || loading || animationLoading}
                  >
                    {gifExporting ? (gifExportProgress || "Recording Dashboard…") : `Download Animation`}
                  </button>
                </>
              ) : null}

              {timeStep !== "LTMA" ? (
                <button
                  style={{
                    ...styles.secondaryButton,
                    ...(compareLoading ? styles.buttonDisabled : {}),
                  }}
                  onClick={handleOpenCompare}
                  disabled={compareLoading}
                >
                  Open compare
                </button>
              ) : null}
            </div>

            {isAnimating ? (
              <button style={styles.secondaryButton} onClick={stopAnimation}>
                Stop animation
              </button>
            ) : null}

            {gifExportProgress && !gifExporting ? (
              <div style={{ ...styles.sectionText, marginTop: 4 }}>{gifExportProgress}</div>
            ) : null}
            {error ? <div style={styles.error}>{error}</div> : null}
          </aside>

          <main style={{ ...styles.card, ...styles.main }}>
            <div
              style={{
                textAlign: "center",
                padding: "0 10px",
                marginBottom: 0,
                lineHeight: 1.1,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  color: palette.text,
                  fontSize: 22,
                  lineHeight: 1.1,
                  fontWeight: 500,
                  letterSpacing: "-0.03em",
                }}
              >
                {mainVisualTitle}
              </h2>
              <p
                style={{
                  margin: "0",
                  color: palette.textSoft,
                  fontSize: 12.5,
                  fontWeight: 500,
                }}
              >
                {mainVisualSubtitle}
              </p>
            </div>

            {htmlUrl ? (
              <div
                style={{
                  ...styles.frameWrap,
                  width: "100%",
                  height: headerCompact ? Math.min(frameHeight, 470) : Math.max(frameHeight, 480),
                  minHeight: headerCompact ? Math.min(frameHeight, 470) : Math.max(frameHeight, 480),
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <iframe
                  key={`${htmlUrl}-${countrySankeyHtml.length}`}
                  src={countrySankeyHtml ? undefined : htmlUrl}
                  srcDoc={countrySankeyHtml || undefined}
                  title="WaterTracker Sankey Diagram"
                  style={{
                    ...styles.iframe,
                    width: "100%",
                    minWidth: "100%",
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              </div>
            ) : basinPlotData ? (
              <>
                {isVoltaBasin ? <div style={styles.basinTabs}>
                  <button
                    type="button"
                    style={{
                      ...styles.basinTabButton,
                      ...(basinViewTab === "sankey"
                        ? styles.basinTabButtonActive
                        : {}),
                    }}
                    onClick={() => handleBasinViewTab("sankey")}
                  >
                    Sankey diagram
                  </button>

                  {isVoltaBasin ? (
                    <button
                      type="button"
                      style={{
                        ...styles.basinTabButton,
                        ...(basinViewTab === "animation"
                          ? styles.basinTabButtonActive
                          : {}),
                      }}
                      onClick={() => handleBasinViewTab("animation")}
                    >
                      Animated flow map
                    </button>
                  ) : null}

                  {isVoltaBasin && basinViewTab === "animation" ? (
                    <button
                      type="button"
                      style={styles.basinTabButton}
                      onClick={() =>
                        loadBasinAnimation({
                          force: true,
                          period: riverAnimationCurrentPeriod,
                          mode: riverAnimationMode,
                        })
                      }
                      disabled={basinAnimationLoading}
                    >
                      {basinAnimationLoading ? "Generating..." : "Regenerate"}
                    </button>
                  ) : null}
                </div> : null}

                {!isVoltaBasin || basinViewTab === "sankey" ? (
                  <div
                    ref={basinCanvasRef}
                    style={{
                      ...styles.frameWrap,
                      width: "100%",
                      marginTop: 0,
                      background: "#ffffff",
                      overflow: "hidden",
                      padding: 0,
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: headerCompact ? "clamp(360px, 48vh, 480px)" : "clamp(440px, 64vh, 620px)",
                        minHeight: headerCompact ? 360 : 440,
                        position: "relative",
                        margin: "0 auto",
                        background: "#ffffff",
                      }}
                    >
                      <Plot
                        data={[{
                          type: "sankey",
                          orientation: "h",
                          // Preserve the WaterTracker/YAML node layout whenever
                          // complete coordinates are available. Fall back to
                          // Plotly's snap layout only when coordinates are absent.
                          arrangement: basinHasDisplayCoordinates ? "freeform" : "snap",
                          // Match plotting.py's backend Sankey plotting domain.
                          domain: { x: [0, 1], y: [0.10, 0.87] },
                          // Plotly applies Sankey label typography at the trace level.
                          // Keep the emphasis subtle: slightly larger, darker country names.
                          textfont: {
                            family: "Inter, Arial, sans-serif",
                            size: 14,
                            color: "#173f7a",
                          },
                          node: {
                            label: basinNodeLabels,
                            color: basinNodeColors,
                            customdata: basinPlotData?.nodes?.hover || [],
                            ...(basinHasDisplayCoordinates
                              ? { x: basinDisplayCoordinates.x, y: basinDisplayCoordinates.y }
                              : {}),
                            // Match backend node geometry; do not alter YAML coordinates.
                            pad: 58,
                            thickness: 12,
                            line: { color: "rgba(16,32,51,0.28)", width: 1.15 },
                            hovertemplate: "%{customdata}<extra></extra>",
                          },
                          link: {
                            source: basinPlotData?.links?.source || [],
                            target: basinPlotData?.links?.target || [],
                            value: basinPlotData?.links?.value || [],
                            color: basinLinkColors,
                            label: basinPlotData?.links?.label || [],
                            customdata: basinPlotData?.links?.percent_out || [],
                            hovertemplate:
                              "%{label}<br>" +
                              "Flow: %{value:.2f} " +
                              (basinPlotData?.meta?.flow_unit || "km³") +
                              "/" +
                              (basinPlotData?.meta?.time_unit || "year") +
                              "<br>%{customdata:.1f}% of total outflow<extra></extra>",
                          },
                        }]}
                        onAfterPlot={(plot) => {
                          const highlightBasinNodeLabels = () => {
                            if (!plot?.querySelectorAll) return;

                            const selectors = [
                              ".sankey g.node text",
                              ".sankey .node text",
                              "g.sankey g.node text",
                            ];

                            const labels = new Set();
                            selectors.forEach((selector) => {
                              plot.querySelectorAll(selector).forEach((text) => labels.add(text));
                            });

                            labels.forEach((text) => {
                              text.style.setProperty("font-weight", "700", "important");
                              text.style.setProperty("font-size", "15px", "important");
                              text.style.setProperty("fill", "#173f7a", "important");
                              text.style.setProperty("font-family", "Inter, Arial, sans-serif", "important");
                            });
                          };

                          highlightBasinNodeLabels();
                          window.requestAnimationFrame(highlightBasinNodeLabels);
                          window.setTimeout(highlightBasinNodeLabels, 80);
                          window.setTimeout(highlightBasinNodeLabels, 300);
                        }}
                        layout={{
                          autosize: true,
                          // Backend plot uses compact outer margins.
                          margin: { l: 10, r: 10, t: 30, b: 2 },
                          font: {
                            family: "Inter, Arial, sans-serif",
                            size: 14,
                            color: palette.text,
                          },
                          paper_bgcolor: "#ffffff",
                          plot_bgcolor: "#ffffff",
                          hoverlabel: {
                            bgcolor: "#102033",
                            bordercolor: "#102033",
                            font: { color: "#ffffff", size: 12 },
                          },
                        }}
                        config={{
                          responsive: true,
                          displaylogo: false,
                          modeBarButtonsToRemove: ["lasso2d", "select2d"],
                        }}
                        useResizeHandler
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.riverViewModeTabs}>
                      <button
                        type="button"
                        style={{
                          ...styles.riverViewModeTab,
                          ...(riverViewMode === "animation"
                            ? styles.riverViewModeTabActive
                            : {}),
                        }}
                        onClick={() => {
                          setRiverViewMode("animation");
                          setRiverAnimationPlaying(false);
                        }}
                      >
                        Animation
                      </button>

                      <button
                        type="button"
                        style={{
                          ...styles.riverViewModeTab,
                          ...(riverViewMode === "comparison"
                            ? styles.riverViewModeTabActive
                            : {}),
                        }}
                        onClick={() => {
                          setRiverViewMode("comparison");
                          setRiverAnimationPlaying(false);

                          const currentIndex = Math.min(
                            Math.max(riverAnimationIndex, 0),
                            Math.max(riverAnimationPeriods.length - 1, 0)
                          );

                          const secondIndex =
                            riverAnimationPeriods.length > 1
                              ? (currentIndex + 1) %
                                riverAnimationPeriods.length
                              : currentIndex;

                          setRiverCompareLeftIndex(currentIndex);
                          setRiverCompareRightIndex(secondIndex);
                        }}
                      >
                        Comparison
                      </button>
                    </div>

                    {riverViewMode === "animation" ? (
                      <div style={styles.riverAnimationControlsCard}>
                        <div style={styles.riverModeSelectWrap}>
                          <span style={styles.riverModeSelectLabel}>
                            Playback mode
                          </span>

                          <select
                            style={styles.riverModeSelect}
                            value={riverAnimationMode}
                            onChange={(event) => {
                              const nextMode = event.target.value;
                              setRiverAnimationPlaying(false);
                              setRiverAnimationMode(nextMode);
                              resetRiverAnimationIndex(nextMode);
                              setRiverCompareLeftIndex(0);
                              setRiverCompareRightIndex(1);
                            }}
                          >
                            <option value="fixed-year-months">
                              Monthly progression · selected year
                            </option>
                            <option value="fixed-month-years">
                              Same month · across years
                            </option>
                            <option value="annual-years">
                              Annual progression · across years
                            </option>
                          </select>
                        </div>

                        <div style={styles.riverPlaybackRow}>
                          <div style={styles.riverPlaybackButtons}>
                            <button
                              type="button"
                              style={styles.riverPlaybackButton}
                              onClick={() => {
                                setRiverAnimationPlaying(false);
                                stepRiverAnimation(-1);
                              }}
                            >
                              ‹ Previous
                            </button>

                            <button
                              type="button"
                              style={{
                                ...styles.riverPlaybackButton,
                                ...styles.riverPlaybackButtonPrimary,
                              }}
                              onClick={toggleRiverAnimationPlayback}
                            >
                              {riverAnimationPlaying ? "❚❚ Pause" : "▶ Play"}
                            </button>

                            <button
                              type="button"
                              style={styles.riverPlaybackButton}
                              onClick={() => {
                                setRiverAnimationPlaying(false);
                                stepRiverAnimation(1);
                              }}
                            >
                              Next ›
                            </button>
                          </div>

                          <div style={styles.riverPeriodChip}>
                            {riverAnimationCurrentPeriod?.label || "—"}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {riverViewMode === "animation" ? (
                    <div
                      style={{
                        ...styles.animationWorkspace,
                        ...responsiveAnimationWorkspace,
                      }}
                    >
                    <section style={styles.animationMapPanel}>
                      <div
                        style={{
                          ...styles.animationMapStage,
                          ...responsiveAnimationStage,
                        }}
                      >
                        {basinAnimationLoading ? (
                          <div style={styles.animationLoading}>
                            Preparing animated basin flow map for{" "}
                            {riverAnimationCurrentPeriod?.label || year}...
                          </div>
                        ) : basinAnimationError ? (
                          <div style={styles.animationError}>
                            {basinAnimationError}
                          </div>
                        ) : basinAnimationUrl ? (
                          <iframe
                            ref={basinAnimationFrameRef}
                            key={`${riverAnimationCurrentPeriod?.start || "period"}-${basinAnimationData?.requestToken || basinAnimationUrl}`}
                            src={basinAnimationHtml ? undefined : basinAnimationUrl}
                            srcDoc={basinAnimationHtml || undefined}
                            title={`${basin} animated river flow map`}
                            style={styles.animationIframe}
                            allowFullScreen
                          />
                        ) : (
                          <div style={styles.animationLoading}>
                            Generate the animated flow map to view the river
                            values.
                          </div>
                        )}
                      </div>
                    </section>

                    <aside style={styles.animationSidePanel}>
                      <div style={styles.flowAnalysisButtonPanel}>
                        <div style={styles.flowAnalysisButtonIcon}>✦</div>

                        <div>
                          <p style={styles.flowAnalysisButtonEyebrow}>
                            AI-powered interpretation
                          </p>

                          <h3 style={styles.flowAnalysisButtonTitle}>
                            AI Flow Analysis
                          </h3>
                        </div>

                        <p style={styles.flowAnalysisButtonText}>
                          Generate an interpretation of the selected basin,
                          animation period, map behaviour, and displayed
                          river-flow values.
                        </p>

                        <button
                          type="button"
                          style={{
                            ...styles.flowAnalysisPrimaryButton,
                            ...(!basinAnimationUrl
                              ? styles.flowAnalysisPrimaryButtonDisabled
                              : {}),
                          }}
                          onClick={() => setFlowAnalysisOpen(true)}
                          disabled={!basinAnimationUrl}
                        >
                          ✦ AI Flow Analysis
                        </button>
                      </div>
                    </aside>
                  </div>
                    ) : (
                      <div
                        style={{
                          ...styles.riverCompareGrid,
                          gridTemplateColumns: isSmallScreen
                            ? "1fr"
                            : "1fr 1fr",
                        }}
                      >
                        <section style={styles.riverComparePanel}>
                          <div style={styles.riverCompareHeader}>
                            <p style={styles.riverCompareTitle}>
                              Comparison A
                            </p>

                            <select
                              style={styles.compactInput}
                              value={riverCompareLeftIndex}
                              onChange={(event) =>
                                setRiverCompareLeftIndex(
                                  Number(event.target.value)
                                )
                              }
                            >
                              {riverAnimationPeriods.map((item, index) => (
                                <option key={`left-${item?.label}`} value={index}>
                                  {item?.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div
                            style={{
                              ...styles.animationMapStage,
                              ...responsiveAnimationStage,
                              minHeight: isSmallScreen ? 430 : 620,
                            }}
                          >
                            {riverCompareLoading ? (
                              <div style={styles.animationLoading}>
                                Loading{" "}
                                {riverAnimationPeriods[riverCompareLeftIndex]
                                  ?.label || "comparison A"}
                                ...
                              </div>
                            ) : riverCompareError ? (
                              <div style={styles.animationError}>
                                {riverCompareError}
                              </div>
                            ) : riverCompareLeftUrl ? (
                              <iframe
                                key={`left-${riverAnimationPeriods[riverCompareLeftIndex]?.start}-${riverCompareLeftUrl}`}
                                src={
                                  riverCompareLeftHtml
                                    ? undefined
                                    : riverCompareLeftUrl
                                }
                                srcDoc={riverCompareLeftHtml || undefined}
                                title={`${basin} river comparison A`}
                                style={styles.animationIframe}
                                allowFullScreen
                              />
                            ) : (
                              <div style={styles.animationLoading}>
                                Select a period for comparison A.
                              </div>
                            )}
                          </div>
                        </section>

                        <section style={styles.riverComparePanel}>
                          <div style={styles.riverCompareHeader}>
                            <p style={styles.riverCompareTitle}>
                              Comparison B
                            </p>

                            <select
                              style={styles.compactInput}
                              value={riverCompareRightIndex}
                              onChange={(event) =>
                                setRiverCompareRightIndex(
                                  Number(event.target.value)
                                )
                              }
                            >
                              {riverAnimationPeriods.map((item, index) => (
                                <option key={`right-${item?.label}`} value={index}>
                                  {item?.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div
                            style={{
                              ...styles.animationMapStage,
                              ...responsiveAnimationStage,
                              minHeight: isSmallScreen ? 430 : 620,
                            }}
                          >
                            {riverCompareLoading ? (
                              <div style={styles.animationLoading}>
                                Loading{" "}
                                {riverAnimationPeriods[riverCompareRightIndex]
                                  ?.label || "comparison B"}
                                ...
                              </div>
                            ) : riverCompareError ? (
                              <div style={styles.animationError}>
                                {riverCompareError}
                              </div>
                            ) : riverCompareRightUrl ? (
                              <iframe
                                key={`right-${riverAnimationPeriods[riverCompareRightIndex]?.start}-${riverCompareRightUrl}`}
                                src={
                                  riverCompareRightHtml
                                    ? undefined
                                    : riverCompareRightUrl
                                }
                                srcDoc={riverCompareRightHtml || undefined}
                                title={`${basin} river comparison B`}
                                style={styles.animationIframe}
                                allowFullScreen
                              />
                            ) : (
                              <div style={styles.animationLoading}>
                                Select a period for comparison B.
                              </div>
                            )}
                          </div>
                        </section>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div
                style={{
                  ...styles.empty,
                  height: isSmallScreen ? 420 : 620,
                  minHeight: isSmallScreen ? 420 : 620,
                }}
              >
                <div style={styles.emptyBadge}>💧</div>
                <h3 style={styles.emptyTitle}>No chart generated yet</h3>
                <p style={styles.emptyText}>
                  Choose Country or Basin, configure the sidebar, and generate a
                  Sankey diagram.
                </p>
              </div>
            )}

            {showSankeyAiPanel ? (
              <section style={styles.aiAgent}>
                <div style={styles.aiAgentHeader}>
                  <div style={styles.aiIdentity}>
                    <div style={styles.aiAvatar}>🤖</div>

                    <div>
                      <h3 style={styles.aiTitle}>
                        AI Water Flow Analyst
                      </h3>

                      <p style={styles.aiSubtitle}>
                        {selectionType === "basin"
                          ? "Technical interpretation of terminal basin outflow and the water-transfer pathways shown in the Sankey diagram."
                          : "AI interpretation of inflows, outflows, percentages, dominant pathways, and the generated Sankey diagram."}
                      </p>
                    </div>
                  </div>

                  <div style={styles.aiActions}>
                    <button
                      type="button"
                      style={{
                        ...styles.aiButton,
                        ...(isAiAgentRunning
                          ? styles.buttonDisabled
                          : {}),
                      }}
                      onClick={startAiAgent}
                      disabled={isAiAgentRunning}
                    >
                      {isAiAgentRunning
                        ? "🔊 Explaining..."
                        : "▶ Start AI explanation"}
                    </button>

                    <button
                      type="button"
                      style={styles.aiStopButton}
                      onClick={stopAiAgent}
                      disabled={!isSpeaking && !isAiAgentRunning}
                    >
                      ■ Stop voice
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.aiStatusGrid,
                    ...responsiveAiGrid,
                    ...(selectionType === "basin"
                      ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
                      : {}),
                  }}
                >
                  {selectionType !== "basin" ? (
                    <div style={styles.aiStatusCard}>
                      <p style={styles.aiStatusTitle}>Total inflow</p>
                      <p style={styles.aiStatusValue}>{inflowDisplay}</p>
                    </div>
                  ) : null}

                  <div style={styles.aiStatusCard}>
                    <p style={styles.aiStatusTitle}>
                      {selectionType === "basin" ? "Outflow" : "Total outflow"}
                    </p>
                    <p style={styles.aiStatusValue}>{outflowDisplay}</p>
                  </div>

                  <div style={styles.aiStatusCard}>
                    <p style={styles.aiStatusTitle}>Main pathway</p>
                    <p style={styles.aiStatusValue}>{topPathwayDisplay}</p>
                  </div>
                </div>

                <div style={styles.aiNarrationBox}>
                  <p style={styles.aiNarrationTitle}>
                    AI Sankey summary
                  </p>

                  <p style={styles.aiNarrationText}>
                    {aiSummary}
                  </p>
                </div>
              </section>
            ) : null}

          </main>
        </div>
      </div>

      {flowAnalysisOpen ? (
        <div
          style={styles.flowAnalysisBackdrop}
          onClick={() => setFlowAnalysisOpen(false)}
          role="presentation"
        >
          <section
            style={styles.flowAnalysisModal}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="flow-analysis-title"
          >
            <header style={styles.flowAnalysisModalHeader}>
              <div style={styles.flowAnalysisModalIdentity}>
                <div style={styles.flowAnalysisModalIcon}>🤖</div>

                <div>
                  <h2
                    id="flow-analysis-title"
                    style={styles.flowAnalysisModalTitle}
                  >
                    AI River Flow Analysis
                  </h2>

                  <p style={styles.flowAnalysisModalSubtitle}>
                    {basin} basin · {animationPeriodLabel}
                  </p>
                </div>
              </div>

              <button
                type="button"
                style={styles.flowAnalysisCloseButton}
                onClick={() => setFlowAnalysisOpen(false)}
                aria-label="Close AI flow analysis"
              >
                ×
              </button>
            </header>

            <div style={styles.flowAnalysisModalBody}>
              <div
                style={{
                  ...styles.flowAnalysisMetaGrid,
                  gridTemplateColumns: isSmallScreen
                    ? "1fr"
                    : "repeat(3, minmax(0, 1fr))",
                }}
              >
                <div style={styles.flowAnalysisMetaCard}>
                  <p style={styles.flowAnalysisMetaLabel}>Basin</p>
                  <p style={styles.flowAnalysisMetaValue}>{basin}</p>
                </div>

                <div style={styles.flowAnalysisMetaCard}>
                  <p style={styles.flowAnalysisMetaLabel}>
                    Analysis period
                  </p>
                  <p style={styles.flowAnalysisMetaValue}>
                    {animationPeriodLabel}
                  </p>
                </div>

                <div style={styles.flowAnalysisMetaCard}>
                  <p style={styles.flowAnalysisMetaLabel}>
                    Playback mode
                  </p>
                  <p style={styles.flowAnalysisMetaValue}>
                    {animationModeLabel}
                  </p>
                </div>
              </div>

              <div style={styles.flowAnalysisSummaryBox}>
                <p style={styles.flowAnalysisSummaryLabel}>
                  AI-generated flow summary
                </p>

                <p style={styles.flowAnalysisSummaryText}>
                  {aiSummary}
                </p>
              </div>
            </div>

            <footer style={styles.flowAnalysisModalFooter}>
              <button
                type="button"
                style={styles.flowAnalysisDoneButton}
                onClick={() => setFlowAnalysisOpen(false)}
              >
                Done
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {compareOpen ? (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: selectionType === "basin" ? 1680 : styles.modal.maxWidth, ...(isSmallScreen ? { width: "98vw", height: "96dvh", gridTemplateRows: "auto auto minmax(0, 1fr)" } : {}) }}>
            <div style={styles.modalHeader}>
              <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                <h3 style={styles.modalTitle}>
                  {selectionType === "basin"
                    ? `Compare ${basins.find((item) => String(item.id) === String(compareBasin))?.name || selectedName || "basin"}${compareTimeStep === "LTMM" ? " · Long-term mean monthly" : compareTimeStep === "LTMA" ? " · Long-term mean annual" : compareTimeStep === "Monthly" ? " by month and year" : " by year"}`
                    : `Compare ${compareCountry || country || "country"}${compareTimeStep === "LTMM" ? " · Long-term mean monthly" : compareTimeStep === "LTMA" ? " · Long-term mean annual" : compareTimeStep === "Monthly" ? " by month and year" : " by year"}`}
                </h3>
                <p style={styles.modalText}>
                  {compareTimeStep === "LTMM"
                    ? "Long-term mean monthly comparison: choose a separate month for the left and right Sankey. The long-term mean period stays fixed."
                    : compareTimeStep === "LTMA"
                    ? "Long-term mean annual values are climatological and do not vary by year. Comparison is disabled for this time step."
                    : compareTimeStep === "Monthly"
                    ? "Monthly comparison: compare the selected month and year on each side. The selected country or basin stays the same."
                    : "Annual comparison: country or basin and time step stay the same. Only the selected year changes between the left and right chart."}
                </p>
              </div>

              <button
                type="button"
                style={{ ...styles.secondaryButton, width: 110, minWidth: 110, flexShrink: 0, padding: "10px 12px", position: "relative", zIndex: 3 }}
                onClick={handleCloseCompare}
              >
                Close
              </button>
            </div>

            <div style={styles.modalControls}>
              <span style={styles.controlGroupLabel}>{selectionType === "basin" ? "Basin" : "Country"}</span>

              {selectionType === "basin" ? (
                <select
                  style={styles.compactInput}
                  value={compareBasin}
                  onChange={(e) => setCompareBasin(e.target.value)}
                  disabled={basinsLoading || !basins.length}
                >
                  {basinsLoading ? (
                    <option value="">Loading basins...</option>
                  ) : !basins.length ? (
                    <option value="">No basins available</option>
                  ) : (
                    basins.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))
                  )}
                </select>
              ) : (
                <select
                  style={styles.compactInput}
                  value={compareCountry}
                  onChange={(e) => setCompareCountry(e.target.value)}
                  disabled={countriesLoading || !countries.length}
                >
                  {countriesLoading ? (
                    <option value="">Loading countries...</option>
                  ) : !countries.length ? (
                    <option value="">No countries available</option>
                  ) : (
                    countries.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))
                  )}
                </select>
              )}

              <span style={styles.controlGroupLabel}>Time step</span>
              <select
                style={styles.compactInput}
                value={compareTimeStep}
                onChange={(e) => {
                  setCompareTimeStep(e.target.value);
                  setCompareLeftBasinPlotData(null);
                  setCompareRightBasinPlotData(null);
                  setCompareLeftUrl("");
                  setCompareRightUrl("");
                }}
              >
                {(selectionType === "basin"
                  ? (availableTimeSteps || TIME_STEPS)
                  : TIME_STEPS
                ).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>

              {showCompareYear ? (
                <>
                  <span style={styles.controlGroupLabel}>Left year</span>
                  <select
                    style={styles.compactInput}
                    value={compareYearA}
                    onChange={(e) => setCompareYearA(e.target.value)}
                  >
                    {(selectionType === "basin" ? basinYears : YEARS).map((item) => (
                      <option key={`left-${item}`} value={item}>{item}</option>
                    ))}
                  </select>

                  <span style={styles.controlGroupLabel}>Right year</span>
                  <select
                    style={styles.compactInput}
                    value={compareYearB}
                    onChange={(e) => setCompareYearB(e.target.value)}
                  >
                    {(selectionType === "basin" ? basinYears : YEARS).map((item) => (
                      <option key={`right-${item}`} value={item}>{item}</option>
                    ))}
                  </select>
                </>
              ) : null}

              {showCompareMonth ? (
                selectionType === "basin" ? (
                  <>
                    <span style={styles.controlGroupLabel}>Left month</span>
                    <select
                      style={styles.compactInput}
                      value={compareMonthA}
                      onChange={(e) => setCompareMonthA(e.target.value)}
                    >
                      {MONTHS.map((item) => (
                        <option key={`left-month-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <span style={styles.controlGroupLabel}>Right month</span>
                    <select
                      style={styles.compactInput}
                      value={compareMonthB}
                      onChange={(e) => setCompareMonthB(e.target.value)}
                    >
                      {MONTHS.map((item) => (
                        <option key={`right-month-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <span style={styles.controlGroupLabel}>Left month</span>
                    <select
                      style={styles.compactInput}
                      value={compareMonthA}
                      onChange={(e) => setCompareMonthA(e.target.value)}
                    >
                      {MONTHS.map((item) => (
                        <option key={`country-left-month-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <span style={styles.controlGroupLabel}>Right month</span>
                    <select
                      style={styles.compactInput}
                      value={compareMonthB}
                      onChange={(e) => setCompareMonthB(e.target.value)}
                    >
                      {MONTHS.map((item) => (
                        <option key={`country-right-month-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </>
                )
              ) : null}

              <button
                style={{ ...styles.button, ...(compareLoading ? styles.buttonDisabled : {}), width: 130, padding: "10px 12px" }}
                onClick={handleRunComparison}
                disabled={compareLoading || !(selectionType === "basin" ? compareBasin : compareCountry)}
              >
                {compareLoading ? "Loading..." : "Compare"}
              </button>

              {compareTimeStep === "Annual" || compareTimeStep === "Monthly" || compareTimeStep === "LTMM" ? (
                <button
                  style={{ ...styles.secondaryButton, width: 120, padding: "10px 12px" }}
                  onClick={swapComparisonYears}
                >
                  Swap comparison
                </button>
              ) : null}
            </div>

            <div style={{ ...styles.compareGraphArea, ...(isSmallScreen ? { gridTemplateColumns: "minmax(0, 1fr)", overflowY: "auto" } : {}) }}>
              {compareError ? <div style={styles.error}>{compareError}</div> : null}

              <div style={styles.compareCard}>
                <div style={styles.compareCaption}>
                  Comparison A · {selectionType === "basin" ? getBasinCompareCaption(compareYearA, compareMonthA) : getCompareCaption(compareYearA, compareMonthA)}
                </div>
                <div style={styles.compareValues}>
                  <span>Inflow: {compareLeftFlowStats?.inflow !== null && compareLeftFlowStats?.inflow !== undefined ? formatFlow(compareLeftFlowStats.inflow, compareLeftFlowStats.unit || "km³") : "Not available"}</span>
                  <span>Outflow: {compareLeftFlowStats?.outflow !== null && compareLeftFlowStats?.outflow !== undefined ? formatFlow(compareLeftFlowStats.outflow, compareLeftFlowStats.unit || "km³") : "Not available"}</span>
                </div>
                {selectionType === "basin" ? (
                  compareLeftBasinPlotData ? (
                    <div style={{ ...styles.compareFrameWrap, height: compareHeight, minHeight: compareHeight, maxHeight: compareHeight, background: "#fff" }}>
                      {renderCompareBasinPlot(compareLeftBasinPlotData, "compare-basin-left")}
                    </div>
                  ) : (
                    <div style={{ ...styles.compareEmpty, height: compareHeight, minHeight: compareHeight, maxHeight: compareHeight }}>Left basin chart not generated</div>
                  )
                ) : compareLeftUrl ? (
                  <div style={{ ...styles.compareFrameWrap, flex: "1 1 0%", minHeight: 0, height: "auto", maxHeight: "none", overflow: "hidden" }}>
                    <iframe key={`${compareLeftUrl}-${compareYearA}-${compareMonthA}-${compareTimeStep}`} src={compareLeftHtml ? undefined : compareLeftUrl} srcDoc={compareLeftHtml || undefined} title="Comparison Left Graph" scrolling="no" style={{ width: "100%", height: "100%", minHeight: 0, border: "none", display: "block", overflow: "hidden" }} />
                  </div>
                ) : (
                  <div style={{ ...styles.compareEmpty, flex: "1 1 auto", minHeight: 260 }}>Left chart not generated</div>
                )}
              </div>

              <div style={styles.compareCard}>
                <div style={styles.compareCaption}>
                  Comparison B · {selectionType === "basin" ? getBasinCompareCaption(compareYearB, compareMonthB) : getCompareCaption(compareYearB, compareMonthB)}
                </div>
                <div style={styles.compareValues}>
                  <span>Inflow: {compareRightFlowStats?.inflow !== null && compareRightFlowStats?.inflow !== undefined ? formatFlow(compareRightFlowStats.inflow, compareRightFlowStats.unit || "km³") : "Not available"}</span>
                  <span>Outflow: {compareRightFlowStats?.outflow !== null && compareRightFlowStats?.outflow !== undefined ? formatFlow(compareRightFlowStats.outflow, compareRightFlowStats.unit || "km³") : "Not available"}</span>
                </div>
                {selectionType === "basin" ? (
                  compareRightBasinPlotData ? (
                    <div style={{ ...styles.compareFrameWrap, height: compareHeight, minHeight: compareHeight, maxHeight: compareHeight, background: "#fff" }}>
                      {renderCompareBasinPlot(compareRightBasinPlotData, "compare-basin-right")}
                    </div>
                  ) : (
                    <div style={{ ...styles.compareEmpty, height: compareHeight, minHeight: compareHeight, maxHeight: compareHeight }}>Right basin chart not generated</div>
                  )
                ) : compareRightUrl ? (
                  <div style={{ ...styles.compareFrameWrap, flex: "1 1 0%", minHeight: 0, height: "auto", maxHeight: "none", overflow: "hidden" }}>
                    <iframe key={`${compareRightUrl}-${compareYearB}-${compareMonthB}-${compareTimeStep}`} src={compareRightHtml ? undefined : compareRightUrl} srcDoc={compareRightHtml || undefined} title="Comparison Right Graph" scrolling="no" style={{ width: "100%", height: "100%", minHeight: 0, border: "none", display: "block", overflow: "hidden" }} />
                  </div>
                ) : (
                  <div style={{ ...styles.compareEmpty, height: compareHeight, minHeight: compareHeight, maxHeight: compareHeight }}>Right chart not generated</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
